import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useSearchParams } from "react-router-dom";
import {
  Table, Button, Icon, Popup, Label, Checkbox, TextArea, Dropdown,
} from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import { LoadingSpinner, BasicModal, ConfirmDialog, EvidenceTextButton } from "../../../Shared";
import { buildStandardDashboardPdf, drawPill } from "../../../Shared/pdfCharts";
import { Accident, Division, AccidentType, Sector, Employee, Site, ActionPlan, Ocupacion, WorkEnvironment, AccidentMechanism, Deviation, MaterialAgent, WorkProcess, InjuryNature, CiiuActivity, Company } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage, useSite, useCompany } from "../../../../contexts";
import { isMaster, isAdmin } from "../../../../utils/checkPermission";
import { OHS_STATUSES } from "../../../../utils/constants";
import { BodyDiagram } from "../BodyDiagram";
import searchSvg from "../../../../assets/svg/search.svg";
import "./AccidentsTable.scss";

const accidentController     = new Accident();
const divisionController     = new Division();
const accidentTypeController = new AccidentType();
const sectorController       = new Sector();
const employeeController     = new Employee();
const siteController         = new Site();
const actionPlanController   = new ActionPlan();
const ocupacionController    = new Ocupacion();
const workEnvController         = new WorkEnvironment();
const accidentMechanismCtrl     = new AccidentMechanism();
const deviationCtrl             = new Deviation();
const materialAgentCtrl         = new MaterialAgent();
const workProcessCtrl           = new WorkProcess();
const injuryNatureCtrl          = new InjuryNature();
const ciiuActivityCtrl          = new CiiuActivity();
const companyController         = new Company();

const CLASSIFICATION_OPTIONS = [
  { key: "work_accident",    value: "work_accident"    },
  { key: "commute_accident", value: "commute_accident" },
  { key: "incident",         value: "incident"         },
];
const CLASSIFICATION_MIGRATION = {
  "accident": "work_accident",
  "near_miss": "incident",
};
const SEVERITY_OPTIONS = [
  { key: "mild",     value: "mild"     },
  { key: "moderate", value: "moderate" },
  { key: "serious",  value: "serious"  },
  { key: "severe",   value: "severe"   },
  { key: "fatal",    value: "fatal"    },
];
const CASE_STATUS_LEGACY = {
  "Borrador": "draft", "Reportado": "reported", "En investigación": "open",
  "Con plan de acción": "with_plan", "Cerrado": "closed", "Reabierto": "reopened",
};
const CASE_STATUS_OPTIONS = [
  { key: "draft",     value: "draft"     },
  { key: "reported",  value: "reported"  },
  { key: "open",      value: "open"      },
  { key: "with_plan", value: "with_plan" },
  { key: "closed",    value: "closed"    },
  { key: "reopened",  value: "reopened"  },
];
const STATUS_CONFIG = {
  draft:     { label: "Borrador",          color: "#9CA3AF", bg: "#F3F4F6" },
  reported:  { label: "Reportado",         color: "#7B5EA7", bg: "#EDE8F5" },
  open:      { label: "En investigación",  color: "#3F7BC6", bg: "#DCE8F7" },
  with_plan: { label: "Con plan de acción",color: "#D4960F", bg: "#FEF3D6" },
  closed:    { label: "Cerrado",           color: "#2E9461", bg: "#D7EFE0" },
  reopened:  { label: "Reabierto",         color: "#B23030", bg: "#F8D5D5" },
};
const formatAccidentId = (a) => {
  const year = a.date ? new Date(a.date).getFullYear() : new Date().getFullYear();
  const num  = String(a.customId || 0).padStart(4, "0");
  return `AT-${year}-${num}`;
};
const SHIFT_OPTIONS = [
  { key: "morning_shift",   value: "morning_shift"   },
  { key: "afternoon_shift", value: "afternoon_shift" },
  { key: "night_shift",     value: "night_shift"     },
  { key: "day_shift",       value: "day_shift"       },
  { key: "rotating_shift",  value: "rotating_shift"  },
  { key: "overtime",        value: "overtime"        },
  { key: "break",           value: "break"           },
  { key: "not_applicable",  value: "not_applicable"  },
];
const POSITION_TENURE_OPTIONS = [
  { key: "less_6m",  value: "less_6m"  },
  { key: "6m_1y",    value: "6m_1y"    },
  { key: "1_5y",     value: "1_5y"     },
  { key: "5_10y",    value: "5_10y"    },
  { key: "more_10y", value: "more_10y" },
];
const CONTRACT_TYPE_OPTIONS = [
  { key: "permanent",  value: "permanent"  },
  { key: "fixed_term", value: "fixed_term" },
  { key: "eventual",   value: "eventual"   },
  { key: "intern",     value: "intern"     },
  { key: "contractor", value: "contractor" },
];
const GENDER_LEGACY = { "masculino": "male", "femenino": "female", "otro": "other_gender" };
const GENDER_OPTIONS = [
  { key: "male",         value: "male"         },
  { key: "female",       value: "female"       },
  { key: "other_gender", value: "other_gender" },
];
const CL_LEGACY = { "propio": "own_employee", "contratista": "contractor", "pasante": "intern", "visitante": "visitor" };
const CONTRACTUAL_LINK_OPTIONS = [
  { key: "own_employee", value: "own_employee" },
  { key: "contractor",   value: "contractor"   },
  { key: "intern",       value: "intern"       },
  { key: "visitor",      value: "visitor"      },
];
const LOCATION_TYPE_OPTIONS = [
  { key: "1_habitual",            value: "1_habitual"            },
  { key: "2_other_establishment", value: "2_other_establishment" },
  { key: "3_outside",             value: "3_outside"             },
];
const YES_NO_OPTIONS = [
  { key: "yes", value: "yes" },
  { key: "no",  value: "no"  },
];
const TOOL_CONDITION_OPTIONS = [
  { key: "good",        value: "good"        },
  { key: "defective",   value: "defective"   },
  { key: "maintenance", value: "maintenance" },
  { key: "na",          value: "na"          },
];
const CAPA_TYPE_OPTIONS = [
  { key: "corrective", value: "corrective" },
  { key: "preventive", value: "preventive" },
];
const CONTROL_HIERARCHY_OPTIONS = [
  { key: "1_elimination",    value: "1_elimination"    },
  { key: "2_substitution",   value: "2_substitution"   },
  { key: "3_engineering",    value: "3_engineering"    },
  { key: "4_administrative", value: "4_administrative" },
  { key: "5_epp",            value: "5_epp"            },
];
const CAPA_STATUS_OPTIONS = [
  { key: "pending",     value: "pending"     },
  { key: "in_progress", value: "in_progress" },
  { key: "closed",      value: "closed"      },
  { key: "overdue",     value: "overdue"     },
];
const EFFICACY_OPTIONS = [
  { key: "pending",             value: "pending"             },
  { key: "effective",           value: "effective"           },
  { key: "partially_effective", value: "partially_effective" },
  { key: "ineffective",         value: "ineffective"         },
];
const GLOBAL_RESULT_OPTIONS = [
  { key: "pending",             value: "pending"             },
  { key: "effective",           value: "effective"           },
  { key: "partially_effective", value: "partially_effective" },
  { key: "reopen",              value: "reopen"              },
];
const ART_NOTIFIED_OPTIONS = [
  { key: "pending",      value: "pending"      },
  { key: "yes",          value: "yes"          },
  { key: "not_required", value: "not_required" },
];
const LABOR_AUTH_OPTIONS = [
  { key: "not_required", value: "not_required" },
  { key: "yes",          value: "yes"          },
  { key: "pending",      value: "pending"      },
];
const CLIENT_NOTIFIED_OPTIONS = [
  { key: "not_applicable", value: "not_applicable" },
  { key: "yes",            value: "yes"            },
  { key: "pending",        value: "pending"        },
];
const INTERNAL_COMM_OPTIONS = [
  { key: "pending",        value: "pending"        },
  { key: "yes_campaign",   value: "yes_campaign"   },
  { key: "not_applicable", value: "not_applicable" },
];
const SST_COMMITTEE_OPTIONS = [
  { key: "pending",        value: "pending"        },
  { key: "yes",            value: "yes"            },
  { key: "not_applicable", value: "not_applicable" },
];
const METHODOLOGY_OPTIONS = [
  { key: "5_whys",     value: "5_whys"     },
  { key: "fishbone",   value: "fishbone"   },
  { key: "cause_tree", value: "cause_tree" },
  { key: "bowtie",     value: "bowtie"     },
];
const BOWTIE_SEVERITY_OPTIONS = [
  { key: "mild",         value: "mild"         },
  { key: "moderate",     value: "moderate"     },
  { key: "severe",       value: "severe"       },
  { key: "very_severe",  value: "very_severe"  },
  { key: "catastrophic", value: "catastrophic" },
];
const RECORD_STATUS_OPTIONS = OHS_STATUSES.map((s) => ({ key: s.key, value: s.value }));
const BODY_PART_GROUPS = [
  {
    label: "Cabeza y cuello",
    parts: ["Cráneo / cuero cabelludo", "Cara", "Ojo(s)", "Oído(s)", "Dientes", "Cerebro", "Cuello"],
  },
  {
    label: "Tronco",
    parts: ["Espalda — Zona dorsal", "Espalda — Zona lumbar", "Médula espinal", "Tórax / costillas", "Cadera / Pelvis", "Abdomen"],
  },
  {
    label: "Miembro superior",
    parts: ["Hombro", "Brazo (región)", "Codo", "Antebrazo", "Muñeca", "Mano", "Pulgar", "Otros dedos de las manos"],
  },
  {
    label: "Miembro inferior",
    parts: ["Muslo", "Rodilla", "Pierna", "Tobillo", "Pie", "Dedos de los pies", "Glúteos"],
  },
];
const BODY_PARTS = BODY_PART_GROUPS.flatMap((g) => g.parts);

// SVG zone → checklist items it represents
const DIAGRAM_PART_MAP = {
  "Cabeza":         ["Cráneo / cuero cabelludo", "Cerebro"],
  "Cara":           ["Cara", "Ojo(s)", "Oído(s)", "Dientes"],
  "Cuello":         ["Cuello"],
  "Hombros":        ["Hombro"],
  "Tronco/Espalda": ["Espalda — Zona dorsal", "Espalda — Zona lumbar", "Médula espinal", "Tórax / costillas", "Abdomen"],
  "Brazos":         ["Brazo (región)", "Codo", "Antebrazo"],
  "Manos":          ["Muñeca", "Mano", "Pulgar", "Otros dedos de las manos"],
  "Caderas":        ["Cadera / Pelvis", "Glúteos"],
  "Piernas":        ["Muslo", "Pierna"],
  "Rodillas":       ["Rodilla"],
  "Tobillos":       ["Tobillo"],
  "Pies":           ["Pie", "Dedos de los pies"],
};
const CLINICAL_SEVERITY_OPTIONS = [
  { key: "mild",     value: "mild"     },
  { key: "moderate", value: "moderate" },
  { key: "severe",   value: "severe"   },
  { key: "critical", value: "critical" },
];
const LATERALITY_OPTIONS = [
  { key: "na",    value: "na"    },
  { key: "left",  value: "left"  },
  { key: "right", value: "right" },
  { key: "both",  value: "both"  },
];
const CASE_TYPE_OPTIONS = [
  { key: "no_injury",            value: "no_injury"            },
  { key: "first_aid_only",       value: "first_aid_only"       },
  { key: "medical_no_days",      value: "medical_no_days"      },
  { key: "with_lost_days",       value: "with_lost_days"       },
  { key: "restricted_work",      value: "restricted_work"      },
  { key: "permanent_disability", value: "permanent_disability" },
  { key: "fatal",                value: "fatal"                },
];
const MEDICAL_ASSISTANCE_OPTIONS = [
  { key: "none",            value: "none"            },
  { key: "first_aid",       value: "first_aid"       },
  { key: "clinic",          value: "clinic"          },
  { key: "hospitalization", value: "hospitalization" },
  { key: "surgery",         value: "surgery"         },
];
const CONTRIBUTING_FACTORS = ["Persona","Tarea","Equipo/Máquina","Entorno/Ambiente","Gestión/Organización"];
const ROOT_CAUSES = [
  { key: "procedure_noncompliance", text: "1. Incumplimiento de procedimientos / normas" },
  { key: "human_error",             text: "2. Error humano / Acción insegura" },
  { key: "equipment_maintenance",   text: "3. Uso y mantenimiento deficiente de equipos / herramientas" },
  { key: "distraction",             text: "4. Distracción / Falta de concentración" },
  { key: "protection_failure",      text: "5. Falla o ausencia en sistemas de protección" },
  { key: "hazardous_exposure",      text: "6. Exposición a agentes peligrosos (químicos, físicos, biológicos)" },
  { key: "unsafe_conditions",       text: "7. Condiciones inseguras del lugar de trabajo (iluminación, orden)" },
  { key: "physical_capacity",       text: "8. Estado físico / capacidad del trabajador" },
  { key: "mental_emotional",        text: "9. Estado mental / emocional del trabajador" },
  { key: "unsafe_behavior",         text: "10. Comportamiento inseguro / incumplimiento voluntario" },
  { key: "lack_technical_skills",   text: "11. Falta de competencias técnicas" },
  { key: "insufficient_training",   text: "12. Capacitación insuficiente o inadecuada" },
  { key: "inadequate_supervision",  text: "13. Supervisión inadecuada" },
  { key: "safety_leadership",       text: "14. Deficiencia en liderazgo de seguridad" },
  { key: "contractor_management",   text: "15. Mala gestión de contratistas / supervisión de terceros" },
  { key: "design_ergonomics",       text: "16. Deficiencia en diseño de instalaciones o ergonomía" },
  { key: "work_planning",           text: "17. Falla en la planificación del trabajo / análisis de riesgos" },
  { key: "procurement",             text: "18. Gestión de compras y materiales deficiente" },
  { key: "ehs_policy",              text: "19. Falta de implementación de políticas EHS" },
  { key: "communication",           text: "20. Deficiencias en la comunicación" },
  { key: "risk_assessment",         text: "21. Falta de evaluación de riesgos previa" },
  { key: "safety_culture",          text: "22. Falla en la cultura de seguridad" },
  { key: "overload_fatigue",        text: "23. Sobrecarga de trabajo / fatiga" },
  { key: "external_conditions",     text: "24. Condiciones externas (clima, tráfico, etc.)" },
  { key: "other",                   text: "25. Otros — describa" },
];


const ESTABLISHMENT_SIZE_OPTIONS = [
  { key: "1-9",     value: "1-9"     },
  { key: "10-49",   value: "10-49"   },
  { key: "50-99",   value: "50-99"   },
  { key: "100-249", value: "100-249" },
  { key: "250-499", value: "250-499" },
  { key: "500+",    value: "500+"    },
];

const EMPTY = {
  // Tab 1 · Establecimiento
  employerName: "", cuit: "", legalAddress: "", employerPhone: "",
  establishmentName: "", establishmentAddress: "", economicActivity: "", establishmentSize: "",
  // Tab 3 · Lugar / Momento
  locationType: "", workEnvironment: "",
  workdayStartTime: "", hoursWorkedBeforeAccident: "",
  isRoutineTask: "", isCommute: "",
  // Localización
  date: "", time: "", shift: "", sector: "", division: "", site: "",
  location: "", process: "", accidentType: "", classification: "",
  initialSeverity: "", briefDescription: "", immediateCause: "", involvedPersons: "",
  toolCondition: "",
  accidentMechanism: "", accidentMechanismOther: "",
  deviation: "", deviationOther: "",
  materialAgentCause: "", materialAgentCauseOther: "",
  materialAgentDeviation: "", materialAgentDeviationOther: "",
  workProcess: "", workProcessOther: "",
  specificActivity: "", objectsUsed: "", claimNumber: "",
  firstAid: "", referral: "", artInsurance: "", lostDays: 0,
  legajo: "", employee: "",
  documento: "", gender: "", birthDate: "", employeeAddress: "",
  contractType: "", contractualLink: "", occupation: "", seniority: "",
  // Tab 5 · Clasificación del caso
  caseType: "", generatedLostDays: "", isFatal: "",
  sickLeaveDate: "", medicalDischargeDate: "", returnToWorkDate: "",
  restrictionDays: "", lostConsciousness: "", fatalityDate: "",
  bodyParts: [], injuryType: "", clinicalSeverity: "", laterality: "", medicalDiagnosis: "", medicalAssistance: "", impactedAreas: [],
  caseStatus: "draft", reportReceptionDate: "", closureDate: "", reopenDate: "", reopenReason: "",
  correctiveActions: [],
  capaVerificationDate: "", capaVerifier: "", capaGlobalResult: "", capaNextReview: "",
  recordStatus: "", generateActionPlan: false, actionPlanNote: "",
  rootCause: {
    narrative: "", priorConditions: "", permitsEPP: "",
    methodology: "5_whys",
    responsiblePerson: "", workerRepParticipated: "", workerRepName: "",
    event: "", whys: ["","","","",""],
    ishikawaManpower: "", ishikawaMachine: "", ishikawaMaterial: "",
    ishikawaMethod: "", ishikawaEnvironment: "", ishikawaMeasurement: "",
    causeTreeLevel0: "", causeTreeLevel1: "", causeTreeLevel2: "", causeTreeLevel3: "",
    bowtieThreats: "", bowtiePreventiveBarriers: "", bowtieFailedBarriers: "",
    bowtieMainEvent: "", bowtieSeverity: "", bowtieConsequences: "",
    bowtieMitigatingBarriers: "", bowtieMissingMitigations: "",
    immediateCauses: "", underlyingCauses: "", latentCauses: "",
    contributingFactors: [], causes: [], causesOther: "", lessonLearned: "",
  },
  witnesses: [],
  // Tab 10 · Cierre
  artNotified: "", artNotificationDate: "",
  laborAuthorityNotified: "",
  clientNotified: "", clientNotificationDetails: "",
  internalCommunicationDone: "", internalCommunicationDate: "",
  sstCommitteeInformed: "", toolboxTalkDone: "",
  files: [],
  lessons: { keyLesson: "", trainings: [], riskMatrix: [], diffusionAreas: "", diffusionDate: "" },
  metrics: { estimatedLostTime: "", directCosts: 0, indirectCosts: 0, confirmedLostDays: 0 },
};

function CollapseSection({ title, children, mt = false }) {
  const [open, setOpen] = React.useState(true);
  return (
    <div className="at-csec" style={mt ? { marginTop: "1.25rem" } : undefined}>
      <button className="at-csec__hdr" type="button" onClick={() => setOpen((o) => !o)}>
        <svg viewBox="0 0 12 12" fill="currentColor" width="9" height="9" style={{ flexShrink: 0, color: "#648EBC", transition: "transform 0.2s", transform: open ? "rotate(0deg)" : "rotate(-90deg)" }}>
          <path d="M6 8L2 4h8z"/>
        </svg>
        {title}
      </button>
      {open && <div className="at-csec__body">{children}</div>}
    </div>
  );
}

const InlineSelect = ({ options, value, onChange, placeholder, addLabel, addValue }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="at-table__inline-select">
    <option value="">{placeholder || "-"}</option>
    {options.map((o) => <option key={o.key} value={o.value}>{o.text}</option>)}
    {addLabel && <option value={addValue || "__add__"}>{addLabel}</option>}
  </select>
);

const FilterDropdown = ({ options, value, onChange, placeholder, addLabel, addValue }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="at-table__fd" ref={ref}>
      <button type="button" className="at-table__fd-toggle" onClick={() => setOpen((v) => !v)}>
        <span className={selected ? "" : "at-table__fd-placeholder"}>{selected ? selected.text : placeholder}</span>
        <span className="at-table__fd-icons">
          {selected && (
            <span
              className="at-table__fd-clear"
              onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
              title={placeholder}
            >
              ✕
            </span>
          )}
          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`at-table__fd-arrow${open ? " at-table__fd-arrow--open" : ""}`}>
            <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
          </svg>
        </span>
      </button>
      {open && (
        <div className="at-table__fd-menu">
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`at-table__fd-item${o.value === value ? " at-table__fd-item--active" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.text}
            </button>
          ))}
          {addLabel && (
            <button
              type="button"
              className="at-table__fd-item at-table__fd-item--add"
              onClick={() => { onChange(addValue || "__add__"); setOpen(false); }}
            >
              {addLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

function InlineCiiuSelect({ options, value, onChange, canAdd, onAdd }) {
  const opts = canAdd
    ? [...options, { key: "__add_ciiu__", value: "__add_ciiu__", text: "+ Nueva actividad CIIU" }]
    : options;
  return (
    <Dropdown
      fluid
      search
      selection
      clearable
      options={opts}
      value={value || ""}
      onChange={(_, { value: v }) => {
        if (v === "__add_ciiu__") { onAdd && onAdd(); }
        else onChange(v || "");
      }}
      placeholder="Seleccionar CIIU"
      noResultsMessage="Sin resultados"
      className="at-table__ciiu-dropdown"
    />
  );
}

function TagInput({ values, onChange, placeholder }) {
  const [input, setInput] = useState("");
  const handleKey = (e) => {
    if (e.key === "Enter" && input.trim()) {
      e.preventDefault();
      onChange([...values, input.trim()]);
      setInput("");
    }
  };
  return (
    <div className="at-table__tag-input">
      {values.map((v, i) => (
        <Label key={i} size="small" style={{ marginBottom: "3px" }}>
          {v}
          <Icon name="delete" onClick={() => onChange(values.filter((_, idx) => idx !== i))} style={{ cursor: "pointer" }} />
        </Label>
      ))}
      <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKey}
        placeholder={placeholder} className="at-table__tag-raw-input" />
    </div>
  );
}

const optLabel = (opts, val) => opts.find((o) => o.value === val)?.text || val || "-";
const formatDate = (d) => {
  if (!d) return "-";
  const iso = typeof d === "string" ? d : new Date(d).toISOString();
  const [y, m, day] = iso.slice(0, 10).split("-");
  return `${day}/${m}/${y}`;
};

// ── Portal modal para "agregar nuevo catálogo" ──────────────────────────────
const PORTAL_INPUT = {
  width: "100%", padding: "10px 14px",
  border: "1px solid rgba(34,36,38,.15)", borderRadius: ".28571429rem",
  fontSize: "14px", outline: "none", background: "#fff",
  color: "rgba(0,0,0,.87)", boxSizing: "border-box", fontFamily: "inherit",
};
const PORTAL_BTN = {
  border: "none", borderRadius: "20px", padding: "10px 30px",
  fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: "pointer",
};

function AddCatalogPortal({ open, title, hasCode = false, onSave, onClose }) {
  const [code,   setCode]   = useState("");
  const [name,   setName]   = useState("");
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  useEffect(() => {
    if (open) { setCode(""); setName(""); setError(""); setSaving(false); }
  }, [open]);

  if (!open) return null;

  const handleSave = async () => {
    if (hasCode && !code.trim()) { setError("El código es requerido."); return; }
    if (!name.trim()) { setError("El nombre es requerido."); return; }
    setSaving(true);
    try {
      const n = name.trim();
      const c = code.trim();
      await onSave({
        code: c ? c.charAt(0).toUpperCase() + c.slice(1).toLowerCase() : c,
        name: n.charAt(0).toUpperCase() + n.slice(1),
      });
      onClose();
    } catch (err) {
      setError(err?.msg || "Error al guardar.");
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", backdropFilter: "blur(2px)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#F8F9FB", borderRadius: "20px", padding: "30px 32px 26px", width: "min(400px, 90vw)", boxShadow: "0 8px 40px rgba(0,0,0,0.15)" }}>
        <p style={{ fontWeight: 700, fontSize: "1.05rem", marginBottom: "20px", color: "#1A2E4A", fontFamily: "'Archivo', sans-serif", margin: "0 0 20px" }}>{title}</p>
        {hasCode && (
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontWeight: 600, fontSize: "13px", color: "#374151", marginBottom: "6px" }}>Código <span style={{ color: "#e53935" }}>*</span></div>
            <input autoFocus value={code} onChange={e => { setCode(e.target.value); setError(""); }}
              placeholder="Ej: AT01" style={PORTAL_INPUT}
              onKeyDown={e => e.key === "Enter" && handleSave()} />
          </div>
        )}
        <div style={{ marginBottom: "0" }}>
          <div style={{ fontWeight: 600, fontSize: "13px", color: "#374151", marginBottom: "6px" }}>Nombre <span style={{ color: "#e53935" }}>*</span></div>
          <input autoFocus={!hasCode} value={name} onChange={e => { setName(e.target.value); setError(""); }}
            placeholder="Ej: Mecánico" style={PORTAL_INPUT}
            onKeyDown={e => e.key === "Enter" && handleSave()} />
        </div>
        {error && <p style={{ color: "#e53935", fontSize: "12px", margin: "6px 0 0" }}>{error}</p>}
        <div style={{ display: "flex", justifyContent: "center", gap: "14px", marginTop: "26px" }}>
          <button onClick={handleSave} disabled={saving}
            style={{ ...PORTAL_BTN, background: "#4A7FA5", color: "#fff", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Guardando..." : "Guardar"}
          </button>
          <button onClick={onClose} style={{ ...PORTAL_BTN, background: "#9BAFC4", color: "#fff" }}>
            Cancelar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function AccidentsTable({ reload, onReload, triggerNew = 0, extYear = null, extSearch = "" }) {
  const { accessToken, user } = useAuth();
  const { translations, language } = useLanguage();
  const { site: contextSite } = useSite();
  const { company: companyFromContext } = useCompany();
  const t = (key) => translations[key] || key;
  const [searchParams, setSearchParams] = useSearchParams();
  const canCreateCiiu = isMaster(user?.role) || isAdmin(user?.role);
  const canManageCatalogs = isMaster(user?.role) || isAdmin(user?.role);
  const isFirstRender = useRef(true);
  const noSiteInCtx    = !contextSite       || contextSite       === "0" || contextSite       === 0;
  const noCompanyInCtx = !companyFromContext || companyFromContext === "0" || companyFromContext === 0;

  const [items,       setItems]       = useState(null);
  const [itemsFilter, setItemsFilter] = useState([]);
  const [search,       setSearch]       = useState("");
  const [typeFilter,   setTypeFilter]   = useState("");
  const [classFilter,  setClassFilter]  = useState("");
  const [statusFilter,   setStatusFilter]   = useState("");
  const [siteFilter,     setSiteFilter]     = useState("");
  const [filterSites,    setFilterSites]    = useState([]);
  const [companySiteIds, setCompanySiteIds] = useState([]);
  const [companyName,    setCompanyName]    = useState("");

  // Catalogs
  const [divisions,        setDivisions]        = useState([]);
  const [accidentTypes,    setAccidentTypes]    = useState([]);
  const [rawAccidentTypes, setRawAccidentTypes] = useState([]);
  const [sites,            setSites]            = useState([]);
  const [sectors,       setSectors]       = useState([]);
  const [employees,      setEmployees]      = useState([]);
  const [ocupaciones,       setOcupaciones]       = useState([]);
  const [workEnvOptions,       setWorkEnvOptions]       = useState([]);
  const [accidentMechOptions,  setAccidentMechOptions]  = useState([]);
  const [deviationOptions,     setDeviationOptions]     = useState([]);
  const [materialAgentOptions, setMaterialAgentOptions] = useState([]);
  const [workProcessOptions,   setWorkProcessOptions]   = useState([]);
  const [injuryNatureOptions,  setInjuryNatureOptions]  = useState([]);
  const [ciiuOptions,          setCiiuOptions]          = useState([]);
  const [addCatalog, setAddCatalog] = useState(null);
  const [employeeStatus, setEmployeeStatus] = useState(null);
  const [empNombre,      setEmpNombre]      = useState("");
  const [empApellido,    setEmpApellido]    = useState("");

  // Inline editing / modal
  const [editingId,     setEditingId]     = useState(null);
  const [editingData,   setEditingData]   = useState(EMPTY);
  const [saving,        setSaving]        = useState(false);
  const [formErrors,    setFormErrors]    = useState({});
  const [activeSection, setActiveSection] = useState(null);
  const [showModal,     setShowModal]     = useState(false);
  const [linkedPlan,    setLinkedPlan]    = useState(null);
  const [planTitle,     setPlanTitle]     = useState("");
  const [creatingPlan,  setCreatingPlan]  = useState(false);
  const [openedAsNew,   setOpenedAsNew]   = useState(false);

  // Export dropdown
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  const [openRowMenu, setOpenRowMenu] = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!openRowMenu) return;
    const handler = (e) => { if (!e.target.closest(".at-table__row-menu, .at-table__row-menu-list")) setOpenRowMenu(null); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openRowMenu]);

  const toggleRowMenu = (id, e) => {
    if (openRowMenu?.id === id) { setOpenRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 120;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setOpenRowMenu({
      id,
      left: rect.right - 190,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  // Confirm dialog
  const [selectedItem, setSelectedItem] = useState(null);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [isDelete,     setIsDelete]     = useState(false);

  // Modales catálogo

  // Modal observaciones
  const [showObs,       setShowObs]       = useState(false);
  const [obsItem,       setObsItem]       = useState(null);
  const [obsVal,        setObsVal]        = useState("");
  const [editingObsIdx, setEditingObsIdx] = useState(null);
  const [editingObsVal, setEditingObsVal] = useState("");
  const [editingObsDate,setEditingObsDate]= useState("");

  // Modales archivos
  const [showAttach,   setShowAttach]   = useState(false);
  const [showFiles,    setShowFiles]    = useState(false);
  const [fileItem,     setFileItem]     = useState(null);
  const [uploading,    setUploading]    = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const fileInputRef = useRef(null);
  const narrativeManualRef        = useRef(false);
  const prevGenerateActionPlanRef = useRef(false);

  // Sync briefDescription → rootCause.narrative while user hasn't manually edited narrative
  useEffect(() => {
    if (!editingId) return;
    if (narrativeManualRef.current) return;
    setEditingData((p) => ({
      ...p,
      rootCause: { ...p.rootCause, narrative: p.briefDescription },
    }));
  }, [editingData.briefDescription, editingId]);

  // Trigger new from parent — solo si hay sitio seleccionado
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (triggerNew > 0 && !noSiteInCtx) startNew();
  }, [triggerNew]);

  // Close export dropdown on outside click
  useEffect(() => {
    if (!showExportMenu) return;
    const handler = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target))
        setShowExportMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showExportMenu]);

  // Load catalogs + employees at mount
  useEffect(() => {
    (async () => {
      const safe = (p) => p.catch(() => []);
      const [divs, types, secs, emps, ocups, wenvs, mechs, devs, mags, wprocs, inats, ciius] = await Promise.all([
        safe(divisionController.getDivisions(accessToken, true)),
        safe(accidentTypeController.getAccidentTypes(accessToken, true)),
        safe(sectorController.getSectors(accessToken, true)),
        safe(employeeController.getEmployees(accessToken, { active: true })),
        safe(ocupacionController.getOcupaciones(accessToken, true)),
        safe(workEnvController.getWorkEnvironments(accessToken, true)),
        safe(accidentMechanismCtrl.getAccidentMechanisms(accessToken, true)),
        safe(deviationCtrl.getDeviations(accessToken, true)),
        safe(materialAgentCtrl.getMaterialAgents(accessToken, true)),
        safe(workProcessCtrl.getWorkProcesses(true)),
        safe(injuryNatureCtrl.getInjuryNatures(true)),
        safe(ciiuActivityCtrl.getCiiuActivities(accessToken, true)),
      ]);
      setDivisions(divs.map((d) => ({ key: d._id, value: d._id, text: d.name })));
      setRawAccidentTypes(types);
      setAccidentTypes(types.map((a) => ({ key: a._id, value: a._id, text: a.name, country: a.country || "" })));
      setSectors(secs.map((s) => ({ key: s._id, value: s._id, text: s.name })));
      setEmployees(emps);
      setOcupaciones(ocups.map((o) => ({ key: o._id, value: o.name, text: o.name })));
      setWorkEnvOptions(wenvs.map((w) => ({ key: w._id, value: w._id, text: w.name })));
      setAccidentMechOptions(mechs.map((m) => ({ key: m._id, value: m._id, text: m.name })));
      setDeviationOptions(devs.map((d) => ({ key: d._id, value: d._id, text: d.name })));
      setMaterialAgentOptions(mags.map((m) => ({ key: m._id, value: m._id, text: m.name })));
      setWorkProcessOptions(wprocs.map((w) => ({ key: w._id, value: w._id, text: w.name })));
      setInjuryNatureOptions(inats.map((i) => ({ key: i._id, value: i._id, text: i.name })));
      setCiiuOptions(ciius.map((c) => ({ key: c._id, value: `${c.code} · ${c.name}`, text: `${c.code} · ${c.name}` })));
    })();
  }, []);


  useEffect(() => {
    if (!accessToken) return;
    const load = !noCompanyInCtx
      ? siteController.getSitesByCompany(accessToken, companyFromContext, true)
      : siteController.getSites(accessToken, true);
    load.then(res => {
      const list = Array.isArray(res) ? res : [];
      setFilterSites(list);
      setSites(list);
      setCompanySiteIds(list.map(s => s._id));
    }).catch(() => { setFilterSites([]); setSites([]); setCompanySiteIds([]); });
  }, [accessToken, companyFromContext, noCompanyInCtx]);

  useEffect(() => { setSiteFilter(""); }, [contextSite, companyFromContext]);

  useEffect(() => {
    if (!accessToken || noCompanyInCtx) { setCompanyName(""); return; }
    companyController.getCompany(accessToken, companyFromContext)
      .then((res) => setCompanyName(res?.name || ""))
      .catch(() => setCompanyName(""));
  }, [accessToken, companyFromContext, noCompanyInCtx]);

  // Auto-fill employee fields when editing opens with an existing legajo
  useEffect(() => {
    if (!editingId || !editingData.legajo || !employees.length) return;
    const v = editingData.legajo.trim().toLowerCase();
    const found = employees.find((e) => e.numero_legajo?.trim().toLowerCase() === v);
    if (found) {
      setEmpNombre(found.nombre || "");
      setEmpApellido(found.apellido || "");
      setField("employee", `${found.nombre} ${found.apellido}`.trim());
      setEmployeeStatus("found");
    }
  }, [editingId]);

  const reloadMechs = async (selectNew = false) => {
    try {
      const prevIds = accidentMechOptions.map((m) => m.key);
      const fetched = await accidentMechanismCtrl.getAccidentMechanisms(accessToken, true);
      const opts = fetched.map((m) => ({ key: m._id, value: m._id, text: m.name }));
      setAccidentMechOptions(opts);
      if (selectNew) { const added = opts.find((o) => !prevIds.includes(o.key)); if (added) setField("accidentMechanism", added.value); }
    } catch {}
  };

  const reloadDeviations = async (selectNew = false) => {
    try {
      const prevIds = deviationOptions.map((d) => d.key);
      const fetched = await deviationCtrl.getDeviations(accessToken, true);
      const opts = fetched.map((d) => ({ key: d._id, value: d._id, text: d.name }));
      setDeviationOptions(opts);
      if (selectNew) { const added = opts.find((o) => !prevIds.includes(o.key)); if (added) setField("deviation", added.value); }
    } catch {}
  };

  const reloadMatAgents = async (field, selectNew = false) => {
    try {
      const prevIds = materialAgentOptions.map((m) => m.key);
      const fetched = await materialAgentCtrl.getMaterialAgents(accessToken, true);
      const opts = fetched.map((m) => ({ key: m._id, value: m._id, text: m.name }));
      setMaterialAgentOptions(opts);
      if (selectNew && field) { const added = opts.find((o) => !prevIds.includes(o.key)); if (added) setField(field, added.value); }
    } catch {}
  };

  const reloadWorkEnvs = async (selectNew = false) => {
    try {
      const prevIds = workEnvOptions.map((w) => w.key);
      const fetched = await workEnvController.getWorkEnvironments(accessToken, true);
      const opts = fetched.map((w) => ({ key: w._id, value: w._id, text: w.name }));
      setWorkEnvOptions(opts);
      if (selectNew) {
        const added = opts.find((o) => !prevIds.includes(o.key));
        if (added) setField("workEnvironment", added.value);
      }
    } catch {}
  };

  const reloadWorkProcesses = async (selectNew = false) => {
    try {
      const prevIds = workProcessOptions.map((w) => w.key);
      const fetched = await workProcessCtrl.getWorkProcesses(true);
      const opts = fetched.map((w) => ({ key: w._id, value: w._id, text: w.name }));
      setWorkProcessOptions(opts);
      if (selectNew) { const added = opts.find((o) => !prevIds.includes(o.key)); if (added) setField("workProcess", added.value); }
    } catch {}
  };

  const reloadInjuryNatures = async (selectNew = false) => {
    try {
      const prevIds = injuryNatureOptions.map((i) => i.key);
      const fetched = await injuryNatureCtrl.getInjuryNatures(true);
      const opts = fetched.map((i) => ({ key: i._id, value: i._id, text: i.name }));
      setInjuryNatureOptions(opts);
      if (selectNew) { const added = opts.find((o) => !prevIds.includes(o.key)); if (added) setField("injuryType", added.value); }
    } catch {}
  };

  const reloadCiiuOptions = async (selectNew = false) => {
    try {
      const prevVals = ciiuOptions.map((c) => c.value);
      const fetched = await ciiuActivityCtrl.getCiiuActivities(accessToken, true);
      const opts = fetched.map((c) => ({ key: c._id, value: `${c.code} · ${c.name}`, text: `${c.code} · ${c.name}` }));
      setCiiuOptions(opts);
      if (selectNew) { const added = opts.find((o) => !prevVals.includes(o.value)); if (added) setField("economicActivity", added.value); }
    } catch {}
  };

  const reloadDivisions = async (selectNew = false) => {
    try {
      const prevIds = divisions.map((d) => d.key);
      const fetched = await divisionController.getDivisions(accessToken, true);
      const opts = fetched.map((d) => ({ key: d._id, value: d._id, text: d.name }));
      setDivisions(opts);
      if (selectNew) { const added = opts.find((o) => !prevIds.includes(o.key)); if (added) setField("division", added.value); }
    } catch {}
  };

  const reloadSectors = async (selectNew = false) => {
    try {
      const prevIds = sectors.map((s) => s.key);
      const fetched = await sectorController.getSectors(accessToken, true);
      const opts = fetched.map((s) => ({ key: s._id, value: s._id, text: s.name }));
      setSectors(opts);
      if (selectNew) { const added = opts.find((o) => !prevIds.includes(o.key)); if (added) setField("sector", added.value); }
    } catch {}
  };

  const reloadOcupaciones = async (selectNew = false) => {
    try {
      const prevVals = ocupaciones.map((o) => o.value);
      const fetched = await ocupacionController.getOcupaciones(accessToken, true);
      const opts = fetched.map((o) => ({ key: o._id, value: o.name, text: o.name }));
      setOcupaciones(opts);
      if (selectNew) { const added = opts.find((o) => !prevVals.includes(o.value)); if (added) setField("occupation", added.value); }
    } catch {}
  };

  const reloadAccidentTypes = async (selectNew = false) => {
    try {
      const prevIds = accidentTypes.map((a) => a.key);
      const fetched = await accidentTypeController.getAccidentTypes(accessToken, true);
      const opts = fetched.map((a) => ({ key: a._id, value: a._id, text: a.name, country: a.country || "" }));
      setAccidentTypes(opts);
      if (selectNew) {
        const added = opts.find((o) => !prevIds.includes(o.key));
        if (added) setField("accidentType", added.value);
      }
    } catch {}
  };


  // Load data
  useEffect(() => {
    (async () => {
      try {
        setItems(null);
        const res = await accidentController.getAccidents(accessToken, true);
        setItems(res);
        setItemsFilter(res);
      } catch {
        setItems([]);
        setItemsFilter([]);
      }
    })();
  }, [reload, accessToken]);

  // Abrir un registro puntual al llegar con ?open=<id> (ej. desde el buscador
  // de evidencias, "Ver documento").
  useEffect(() => {
    if (!items) return;
    const openId = searchParams.get("open");
    if (!openId) return;
    const target = items.find((i) => i._id === openId);
    if (target) startEdit(target);
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [items, searchParams]);

  // Client-side filter
  useEffect(() => {
    if (!items) return;
    let filtered = [...items];
    if (!noSiteInCtx) {
      filtered = filtered.filter((a) => (a.site?._id || a.site) === contextSite);
    } else if (!noCompanyInCtx && companySiteIds.length > 0) {
      filtered = filtered.filter((a) => companySiteIds.includes(a.site?._id || a.site));
      if (siteFilter) filtered = filtered.filter((a) => (a.site?._id || a.site) === siteFilter);
    } else if (siteFilter) {
      filtered = filtered.filter((a) => (a.site?._id || a.site) === siteFilter);
    }
    if (extYear) filtered = filtered.filter((a) => a.date && new Date(a.date).getFullYear() === extYear);
    if (typeFilter)   filtered = filtered.filter((a) => (a.accidentMechanism?._id || a.accidentMechanism) === typeFilter);
    if (classFilter)  filtered = filtered.filter((a) => a.caseType === classFilter);
    if (statusFilter) filtered = filtered.filter((a) => a.caseStatus === statusFilter);
    const q = (extSearch || search).toLowerCase();
    if (q) {
      filtered = filtered.filter((a) =>
        a.employee?.toLowerCase().includes(q) ||
        a.legajo?.toLowerCase().includes(q) ||
        a.location?.toLowerCase().includes(q) ||
        a.accidentType?.name?.toLowerCase().includes(q) ||
        a.sector?.name?.toLowerCase().includes(q) ||
        a.briefDescription?.toLowerCase().includes(q) ||
        a.site?.name?.toLowerCase().includes(q) ||
        formatAccidentId(a).toLowerCase().includes(q) ||
        formatDate(a.date).includes(q)
      );
    }
    setItemsFilter(filtered);
  }, [search, extSearch, typeFilter, classFilter, statusFilter, siteFilter, extYear, items, contextSite, noSiteInCtx, noCompanyInCtx, companySiteIds]);

  const getId = (val) => (val && typeof val === "object" ? val._id : val) || "";

  const startNew = () => {
    narrativeManualRef.current = false;
    prevGenerateActionPlanRef.current = false;
    setEditingId("new");
    const today = new Date().toISOString().slice(0, 10);
    setEditingData({ ...EMPTY, site: contextSite || "", date: today });
    setActiveSection(null);
    setEmployeeStatus(null);
    setEmpNombre(""); setEmpApellido("");
  };

  const startEdit = (item, fromNew = false) => {
    const savedNarrative = item.rootCause?.narrative || "";
    const savedDesc      = item.briefDescription     || "";
    narrativeManualRef.current        = savedNarrative !== "" && savedNarrative !== savedDesc;
    prevGenerateActionPlanRef.current = item.generateActionPlan ?? false;
    setEditingId(item._id);
    setEditingData({
      // Tab 1 · Establecimiento
      employerName:         item.employerName         || "",
      cuit:                 item.cuit                 || "",
      legalAddress:         item.legalAddress         || "",
      employerPhone:        item.employerPhone         || "",
      establishmentName:    item.establishmentName    || "",
      establishmentAddress: item.establishmentAddress || "",
      economicActivity:     item.economicActivity     || "",
      establishmentSize:    item.establishmentSize    || "",
      locationType:              item.locationType              || "",
      workEnvironment:           item.workEnvironment?._id || item.workEnvironment || "",
      workdayStartTime:          item.workdayStartTime          || "",
      hoursWorkedBeforeAccident: item.hoursWorkedBeforeAccident ?? "",
      isRoutineTask:             item.isRoutineTask             || "",
      isCommute:                 item.isCommute                 || "",
      date:             item.date ? item.date.slice(0, 10) : "",
      time:             item.time             || "",
      shift:            item.shift            || "",
      sector:           getId(item.sector),
      division:         getId(item.division),
      site:             contextSite || getId(item.site),
      location:         item.location         || "",
      process:          item.process          || "",
      accidentType:     getId(item.accidentType),
      classification:   CLASSIFICATION_MIGRATION[item.classification] ?? item.classification ?? "",
      initialSeverity:  item.initialSeverity  || "",
      briefDescription: item.briefDescription || "",
      immediateCause:   item.immediateCause   || "",
      involvedPersons:  item.involvedPersons  || "",
      firstAid:         item.firstAid         || "",
      referral:         item.referral         || "",
      artInsurance:     item.artInsurance     || "",
      lostDays:         item.lostDays         ?? 0,
      legajo:           item.legajo           || "",
      employee:         item.employee         || "",
      documento:        item.documento        || "",
      gender:           GENDER_LEGACY[item.gender] || item.gender || "",
      birthDate:        item.birthDate ? item.birthDate.slice(0, 10) : "",
      employeeAddress:  item.employeeAddress  || "",
      contractType:     item.contractType     || "",
      contractualLink:  CL_LEGACY[item.contractualLink] || item.contractualLink || "",
      occupation:       item.occupation       || "",
      seniority:        item.seniority        || "",
      toolCondition:          item.toolCondition          || "",
      accidentMechanism:      item.accidentMechanism?._id      || item.accidentMechanism      || "",
      accidentMechanismOther: item.accidentMechanismOther || "",
      deviation:              item.deviation?._id              || item.deviation              || "",
      deviationOther:         item.deviationOther         || "",
      materialAgentCause:         item.materialAgentCause?._id         || item.materialAgentCause         || "",
      materialAgentCauseOther:    item.materialAgentCauseOther    || "",
      materialAgentDeviation:     item.materialAgentDeviation?._id     || item.materialAgentDeviation     || "",
      materialAgentDeviationOther:item.materialAgentDeviationOther|| "",
      workProcess:            item.workProcess?._id || item.workProcess || "",
      workProcessOther:       item.workProcessOther       || "",
      specificActivity:       item.specificActivity       || "",
      objectsUsed:            item.objectsUsed            || "",
      claimNumber:            item.claimNumber            || "",
      caseType:            item.caseType            || "",
      generatedLostDays:   item.generatedLostDays   || "",
      isFatal:             item.isFatal             || "",
      sickLeaveDate:       item.sickLeaveDate       ? item.sickLeaveDate.slice(0, 10)       : "",
      medicalDischargeDate:item.medicalDischargeDate ? item.medicalDischargeDate.slice(0, 10): "",
      returnToWorkDate:    item.returnToWorkDate    ? item.returnToWorkDate.slice(0, 10)    : "",
      restrictionDays:     item.restrictionDays     ?? "",
      lostConsciousness:   item.lostConsciousness   || "",
      fatalityDate:        item.fatalityDate        ? item.fatalityDate.slice(0, 10)        : "",
      bodyParts:        item.bodyParts        || [],
      injuryType:       item.injuryType?._id || item.injuryType || "",
      clinicalSeverity: item.clinicalSeverity || "",
      laterality:       item.laterality       || "",
      medicalDiagnosis: item.medicalDiagnosis || "",
      medicalAssistance:item.medicalAssistance|| "",
      impactedAreas:    item.impactedAreas    || [],
      caseStatus:          CASE_STATUS_LEGACY[item.caseStatus] || item.caseStatus || "draft",
      reportReceptionDate: item.reportReceptionDate ? item.reportReceptionDate.slice(0, 10) : "",
      closureDate:         item.closureDate         ? item.closureDate.slice(0, 10)         : "",
      reopenDate:          item.reopenDate          ? item.reopenDate.slice(0, 10)          : "",
      reopenReason:        item.reopenReason        || "",
      correctiveActions: item.correctiveActions || [],
      capaVerificationDate: item.capaVerificationDate ? item.capaVerificationDate.slice(0, 10) : "",
      capaVerifier:         item.capaVerifier         || "",
      capaGlobalResult:     item.capaGlobalResult     || "",
      capaNextReview:       item.capaNextReview       ? item.capaNextReview.slice(0, 10) : "",
      rootCause: {
        narrative:              item.rootCause?.narrative              || item.briefDescription || "",
        priorConditions:        item.rootCause?.priorConditions        || "",
        permitsEPP:             item.rootCause?.permitsEPP             || "",
        methodology:            item.rootCause?.methodology            || "5_whys",
        responsiblePerson:      item.rootCause?.responsiblePerson      || "",
        workerRepParticipated:  item.rootCause?.workerRepParticipated  || "",
        workerRepName:          item.rootCause?.workerRepName          || "",
        event:                  item.rootCause?.event                  || "",
        whys:                   item.rootCause?.whys                   || ["","","","",""],
        ishikawaManpower:       item.rootCause?.ishikawaManpower       || "",
        ishikawaMachine:        item.rootCause?.ishikawaMachine        || "",
        ishikawaMaterial:       item.rootCause?.ishikawaMaterial       || "",
        ishikawaMethod:         item.rootCause?.ishikawaMethod         || "",
        ishikawaEnvironment:    item.rootCause?.ishikawaEnvironment    || "",
        ishikawaMeasurement:    item.rootCause?.ishikawaMeasurement    || "",
        causeTreeLevel0:        item.rootCause?.causeTreeLevel0        || "",
        causeTreeLevel1:        item.rootCause?.causeTreeLevel1        || "",
        causeTreeLevel2:        item.rootCause?.causeTreeLevel2        || "",
        causeTreeLevel3:        item.rootCause?.causeTreeLevel3        || "",
        bowtieThreats:          item.rootCause?.bowtieThreats          || "",
        bowtiePreventiveBarriers: item.rootCause?.bowtiePreventiveBarriers || "",
        bowtieFailedBarriers:   item.rootCause?.bowtieFailedBarriers   || "",
        bowtieMainEvent:        item.rootCause?.bowtieMainEvent        || "",
        bowtieSeverity:         item.rootCause?.bowtieSeverity         || "",
        bowtieConsequences:     item.rootCause?.bowtieConsequences     || "",
        bowtieMitigatingBarriers: item.rootCause?.bowtieMitigatingBarriers || "",
        bowtieMissingMitigations: item.rootCause?.bowtieMissingMitigations || "",
        immediateCauses:        item.rootCause?.immediateCauses        || "",
        underlyingCauses:       item.rootCause?.underlyingCauses       || "",
        latentCauses:           item.rootCause?.latentCauses           || "",
        contributingFactors:    item.rootCause?.contributingFactors    || [],
        causes:                 item.rootCause?.causes                 || [],
        causesOther:            item.rootCause?.causesOther            || "",
        lessonLearned:          item.rootCause?.lessonLearned          || "",
      },
      recordStatus:       item.recordStatus       || "",
      generateActionPlan: item.generateActionPlan ?? false,
      actionPlanNote:     item.actionPlanNote     || "",
      witnesses: item.witnesses || [],
      artNotified:               item.artNotified               || "",
      artNotificationDate:       item.artNotificationDate       ? item.artNotificationDate.slice(0, 10) : "",
      laborAuthorityNotified:    item.laborAuthorityNotified    || "",
      clientNotified:            item.clientNotified            || "",
      clientNotificationDetails: item.clientNotificationDetails || "",
      internalCommunicationDone: item.internalCommunicationDone || "",
      internalCommunicationDate: item.internalCommunicationDate ? item.internalCommunicationDate.slice(0, 10) : "",
      sstCommitteeInformed:      item.sstCommitteeInformed      || "",
      toolboxTalkDone:           item.toolboxTalkDone           || "",
      files:                     item.files                     || [],
      lessons: {
        keyLesson:      item.lessons?.keyLesson      || "",
        trainings:      item.lessons?.trainings      || [],
        riskMatrix:     item.lessons?.riskMatrix     || [],
        diffusionAreas: item.lessons?.diffusionAreas || "",
        diffusionDate:  item.lessons?.diffusionDate  ? item.lessons.diffusionDate.slice(0, 10) : "",
      },
      metrics: {
        estimatedLostTime: item.metrics?.estimatedLostTime || "",
        directCosts:       item.metrics?.directCosts       ?? 0,
        indirectCosts:     item.metrics?.indirectCosts     ?? 0,
        confirmedLostDays: item.metrics?.confirmedLostDays ?? 0,
      },
    });
    setActiveSection("establishment");
    setEmployeeStatus(null);
    const parts = (item.employee || "").trim().split(" ");
    setEmpApellido(parts.length > 1 ? parts.pop() : "");
    setEmpNombre(parts.join(" "));
    setLinkedPlan(null);
    actionPlanController.getActionPlanByAccident(accessToken, item._id)
      .then(plan => setLinkedPlan(plan || null))
      .catch(() => {});
    setOpenedAsNew(fromNew);
    setFormErrors({});
    setShowModal(true);
  };

  const cancelEdit = () => { setEditingId(null); setEditingData(EMPTY); setActiveSection(null); setEmployeeStatus(null); setEmpNombre(""); setEmpApellido(""); setShowModal(false); setLinkedPlan(null); setPlanTitle(""); setOpenedAsNew(false); setFormErrors({}); };

  const handleCreatePlan = async () => {
    if (creatingPlan || !editingId || editingId === "new") return;
    const title = planTitle.trim()
      || `Accidente: ${editingData.employee || "Sin empleado"} — ${editingData.date}`;
    setCreatingPlan(true);
    try {
      const created = await actionPlanController.createActionPlan(accessToken, {
        title,
        findingOrigin:      "Accidente",
        moduloOrigen:       "accident",
        accidentId:         editingId,
        findingDescription: editingData.briefDescription || "",
        site:               editingData.site   || undefined,
        sector:             editingData.sector || undefined,
        status:             "pending",
      });
      setLinkedPlan(created.actionPlan);
      setPlanTitle("");
      toast.success("Plan de acción creado y vinculado al accidente");
    } catch (err) {
      toast.error(err?.msg || "No se pudo crear el plan de acción");
    } finally {
      setCreatingPlan(false);
    }
  };

  const setField = (field, value) => {
    setEditingData((p) => ({ ...p, [field]: value }));
    if (formErrors[field]) setFormErrors((p) => { const n = { ...p }; delete n[field]; return n; });
  };
  const errClass = (field) => formErrors[field] ? " has-error" : "";

  const setRootCause = (field, value) => setEditingData((p) => ({ ...p, rootCause: { ...p.rootCause, [field]: value } }));
  const setLessons   = (field, value) => setEditingData((p) => ({ ...p, lessons:   { ...p.lessons,   [field]: value } }));
  const setMetrics   = (field, value) => setEditingData((p) => ({ ...p, metrics:   { ...p.metrics,   [field]: value } }));

  const handleLegajoChange = (value) => {
    setField("legajo", value);
    if (!value.trim()) {
      setEmployeeStatus(null);
      setEmpNombre(""); setEmpApellido("");
      setField("seniority", "");
      return;
    }
    const v = value.trim().toLowerCase();
    const found = employees.find((e) => e.numero_legajo?.trim().toLowerCase() === v);
    if (found) {
      const nombre   = found.nombre   || "";
      const apellido = found.apellido || "";
      setEmpNombre(nombre);
      setEmpApellido(apellido);
      setField("employee", `${nombre} ${apellido}`.trim());
      if (found.dni)   setField("documento",  found.dni);
      if (found.cargo) setField("occupation", found.cargo);
      if (found.fecha_ingreso) {
        const months = Math.floor((Date.now() - new Date(found.fecha_ingreso)) / (1000 * 60 * 60 * 24 * 30.44));
        const seniority =
          months < 6   ? "less_6m" :
          months < 12  ? "6m_1y"   :
          months < 60  ? "1_5y"    :
          months < 120 ? "5_10y"   : "more_10y";
        setField("seniority", seniority);
      }
      const tipoMap = { permanente: "permanent", eventual: "eventual" };
      if (found.tipo && tipoMap[found.tipo]) setField("contractType", tipoMap[found.tipo]);
      setEmployeeStatus("found");
    } else {
      setEmpNombre(""); setEmpApellido("");
      setField("employee", "");
      setField("seniority", "");
      setEmployeeStatus("not_found");
    }
  };

  const handleEmpNombreChange = (val) => {
    setEmpNombre(val);
    setField("employee", `${val} ${empApellido}`.trim());
    if (val.trim() && formErrors.empNombre) setFormErrors((p) => { const n = { ...p }; delete n.empNombre; return n; });
  };

  const handleEmpApellidoChange = (val) => {
    setEmpApellido(val);
    setField("employee", `${empNombre} ${val}`.trim());
    if (val.trim() && formErrors.empApellido) setFormErrors((p) => { const n = { ...p }; delete n.empApellido; return n; });
  };

  const [registeringEmp, setRegisteringEmp] = useState(false);
  const handleRegisterEmployee = async () => {
    if (!editingData.legajo || !empNombre || !empApellido) return;
    setRegisteringEmp(true);
    try {
      const res = await employeeController.createEmployee(accessToken, {
        numero_legajo: editingData.legajo,
        nombre:        empNombre,
        apellido:      empApellido,
        tipo:          "eventual",
        active:        true,
      });
      setEmployees((prev) => [...prev, res.employee || { numero_legajo: editingData.legajo, nombre: empNombre, apellido: empApellido }]);
      setEmployeeStatus("found");
      toast.success("Empleado eventual registrado correctamente");
    } catch (err) {
      toast.error(err?.msg || "Error al registrar el empleado");
    } finally {
      setRegisteringEmp(false);
    }
  };

  const ENUM_FIELDS = [
    "shift", "classification", "initialSeverity", "accidentMechanism",
    "medicalAssistance", "contractType", "contractualLink",
    "gender", "seniority", "establishmentSize", "toolCondition", "recordStatus",
  ];
  // Fields that reference ObjectId — must never be sent as ""
  const OBJECTID_FIELDS = [
    "division", "sector", "accidentType", "site",
    "workEnvironment", "accidentMechanism", "deviation",
    "materialAgentCause", "materialAgentDeviation",
    "workProcess", "injuryType",
  ];
  const buildPayload = (data) => {
    const out = { ...data };
    ENUM_FIELDS.forEach((f) => { if (out[f] === "") delete out[f]; });
    OBJECTID_FIELDS.forEach((f) => { if (!out[f]) delete out[f]; });
    if (out.hoursWorkedBeforeAccident === "" || out.hoursWorkedBeforeAccident === null) delete out.hoursWorkedBeforeAccident;
    return out;
  };

  const REQUIRED_TABS = {
    "Establecimiento": ["employerName", "establishmentName", "establishmentSize"],
    "Empleado":        ["legajo", "empNombre", "empApellido", "contractualLink", "occupation"],
    "Lugar/Momento":   ["date", "time", "locationType"],
    "Descripción":     ["briefDescription", "accidentMechanism", "materialAgentCause", "workProcess"],
    "Clasificación":   ["classification", "caseType"],
    "Impacto":         ["injuryType"],
    "Estado":          ["caseStatus"],
    "Causa Raíz":     ["rc_responsible"],
  };

  const validateForm = () => {
    const errs = {};
    if (!editingData.employerName?.trim())                                                              errs.employerName = true;
    if (!editingData.establishmentName?.trim())                                                         errs.establishmentName = true;
    if (!editingData.establishmentSize)                                                                 errs.establishmentSize = true;
    if (!editingData.legajo?.trim())                                                                    errs.legajo = true;
    if (!empNombre?.trim())                                                                             errs.empNombre = true;
    if (!empApellido?.trim())                                                                           errs.empApellido = true;
    if (!editingData.contractualLink)                                                                   errs.contractualLink = true;
    if (!editingData.occupation)                                                                        errs.occupation = true;
    if (!editingData.date)                                                                              errs.date = true;
    if (!editingData.time)                                                                              errs.time = true;
    if (!editingData.locationType)                                                                      errs.locationType = true;
    if (!editingData.briefDescription?.trim() || editingData.briefDescription.trim().length < 30)      errs.briefDescription = true;
    if (!editingData.accidentMechanism)                                                                 errs.accidentMechanism = true;
    if (!editingData.materialAgentCause)                                                                errs.materialAgentCause = true;
    if (!editingData.workProcess)                                                                       errs.workProcess = true;
    if (!editingData.classification)                                                                    errs.classification = true;
    if (!editingData.caseType)                                                                          errs.caseType = true;
    if (!editingData.injuryType)                                                                        errs.injuryType = true;
    if (!editingData.caseStatus)                                                                        errs.caseStatus = true;
    if (!editingData.rootCause?.responsiblePerson?.trim())                                              errs.rc_responsible = true;
    return errs;
  };

  const handleSave = async () => {
    if (editingId === "new") {
      if (!editingData.date) { toast.error(t("date_required")); return; }
    } else {
      const errs = validateForm();
      if (Object.keys(errs).length > 0) {
        setFormErrors(errs);
        const missingTabs = Object.entries(REQUIRED_TABS)
          .filter(([, fields]) => fields.some((f) => errs[f]))
          .map(([tab]) => tab);
        toast.error(`Completá los campos obligatorios en: ${missingTabs.join(", ")}`);
        return;
      }
      setFormErrors({});
    }
    setSaving(true);
    const payload = buildPayload(editingData);
    try {
      if (editingId === "new") {
        const res = await accidentController.createAccident(accessToken, payload);
        toast.success(t("accident_created"));
        // Reload list and open full form for the new accident
        const newId = res?._id || res?.accident?._id;
        const all = await accidentController.getAccidents(accessToken, true);
        setItems(all);
        setItemsFilter(all);
        onReload();
        const newItem = newId
          ? all.find((i) => i._id === newId)
          : all[all.length - 1];
        if (newItem) startEdit(newItem, true);
        else cancelEdit();
      } else {
        await accidentController.updateAccident(accessToken, editingId, payload);
        toast.success(t("accident_updated"));
        cancelEdit();
        onReload();
      }
    } catch (err) {
      toast.error(err?.msg || t("error_saving"));
    } finally {
      setSaving(false);
    }
  };

  const exportRows = (rows) => rows.map((a, i) => ({
    "N°":         i + 1,
    "ID":         formatAccidentId(a),
    "Fecha":      formatDate(a.date),
    "Sitio":      a.site?.name || "-",
    "División":   a.division?.name || "-",
    "Tipo":       t(a.caseType) || "-",
    "Forma":      a.accidentMechanism?.name || "-",
    "Empleado":   a.employee || "-",
    "Días":       a.lostDays ?? 0,
    "Estado":     t(a.caseStatus) || a.caseStatus || "-",
  }));

  const handleExportCSV = () => {
    const data = exportRows(itemsFilter);
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const csv = [headers.join(","), ...data.map((r) => headers.map((h) => `"${String(r[h]).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "accidentes.csv");
  };

  const handleExportXLSX = () => {
    const data = exportRows(itemsFilter);
    if (!data.length) return;
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Accidentes");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "accidentes.xlsx");
  };

  const handleExportPDF = async () => {
    if (!itemsFilter.length) return;
    const siteName = !noSiteInCtx
      ? sites.find((s) => s._id === contextSite)?.name
      : siteFilter ? sites.find((s) => s._id === siteFilter)?.name : null;

    const tableColumns = [
      { key: "id", header: "ID", flex: 1, value: (r) => formatAccidentId(r) },
      { key: "date", header: t("date"), flex: 0.9, value: (r) => formatDate(r.date) },
      { key: "site", header: t("site"), flex: 1, value: (r) => r.site?.name || "-" },
      { key: "division", header: t("division"), flex: 1, value: (r) => r.division?.name || "-" },
      { key: "type", header: t("type"), flex: 0.9, value: (r) => t(r.caseType) || "-" },
      { key: "mechanism", header: t("acc_col_form"), flex: 1.2, value: (r) => r.accidentMechanism?.name || "-" },
      { key: "employee", header: t("employee"), flex: 1.2, value: (r) => r.employee || "-" },
      { key: "lostDays", header: t("acc_col_days"), flex: 0.7, align: "center", value: (r) => r.lostDays ?? 0 },
      {
        key: "status", header: t("status"), flex: 1.1, align: "center",
        render: (doc, { x, y, width, height, row }) => {
          const cfg = STATUS_CONFIG[row.caseStatus] || STATUS_CONFIG.draft;
          drawPill(doc, { x: x + width / 2, y: y + height / 2, text: t(row.caseStatus) || cfg.label, color: cfg.color });
        },
      },
    ];

    await buildStandardDashboardPdf({
      jsPDFCtor: jsPDF,
      pageTitle: t("accidents") || "Listado de Accidentes",
      companyName: [companyName, siteName].filter(Boolean).join(" · "),
      generatedOnLabel: t("generated_on"),
      table: { columns: tableColumns, rows: itemsFilter, rowHeight: 24, headerHeight: 22, emptyMessage: t("no_results") },
      tableTitle: t("accidents") || "Listado de Accidentes",
      filename: "accidentes.pdf",
    });
  };

  const openDelete = (item) => {
    setSelectedItem(item); setIsDelete(true);
    setShowConfirm(true);
  };
  const openToggle = (item) => {
    setSelectedItem(item); setIsDelete(false);
    setShowConfirm(true);
  };
  const handleConfirm = async () => {
    try {
      if (isDelete) {
        await accidentController.deleteAccident(accessToken, selectedItem._id);
        toast.success(t("accident_deleted"));
      } else {
        await accidentController.updateAccident(accessToken, selectedItem._id, { active: !selectedItem.active });
        toast.success(selectedItem.active ? t("accident_deactivated") : t("accident_activated"));
      }
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || t("error_saving")); }
  };

  const openObs = (item) => { setObsItem(item); setObsVal(""); setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); setShowObs(true); };

  const handleSaveObs = async () => {
    if (!obsVal.trim()) return;
    setSaving(true);
    try {
      const userName = [user?.name, user?.lastname].filter(Boolean).join(" ") || "Usuario";
      const newObs = { text: obsVal.trim(), userName, createdAt: new Date() };
      const updatedObs = [...(obsItem.observations || []), newObs];
      await accidentController.updateAccident(accessToken, obsItem._id, { observations: updatedObs });
      toast.success(t("observations_saved"));
      setShowObs(false);
      onReload();
    } catch { toast.error(t("error_saving")); }
    finally { setSaving(false); }
  };

  const handleSaveObsEdit = async () => {
    if (!editingObsVal.trim()) return;
    setSaving(true);
    try {
      const updatedObs = (obsItem.observations || []).map((o, i) =>
        i === editingObsIdx
          ? { ...o, text: editingObsVal.trim(), createdAt: editingObsDate ? new Date(editingObsDate) : o.createdAt }
          : o
      );
      await accidentController.updateAccident(accessToken, obsItem._id, { observations: updatedObs });
      toast.success(t("observations_saved"));
      setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate("");
      onReload();
    } catch { toast.error(t("error_saving")); }
    finally { setSaving(false); }
  };

  const handleDeleteObs = async (origIdx) => {
    setSaving(true);
    try {
      const updatedObs = (obsItem.observations || []).filter((_, i) => i !== origIdx);
      await accidentController.updateAccident(accessToken, obsItem._id, { observations: updatedObs });
      toast.success(t("observations_saved"));
      onReload();
    } catch { toast.error(t("error_saving")); }
    finally { setSaving(false); }
  };

  const openAttach = (item) => { setFileItem(item); setShowAttach(true); };
  const openFiles  = (item) => { setFileItem(item); setShowFiles(true); };

  const handleUpload = async () => {
    const files = fileInputRef.current?.files;
    if (!files || files.length === 0) { toast.warning(t("select_files_to_attach")); return; }
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("file", file);
        await accidentController.uploadFile(accessToken, fileItem._id, formData);
      }
      toast.success(t("files_uploaded"));
      if (fileInputRef.current) fileInputRef.current.value = "";
      setShowAttach(false);
      onReload();
    } catch { toast.error(t("error_uploading_files")); }
    finally { setUploading(false); }
  };

  const handleDeleteFile = async (fileKey) => {
    setDeletingFile(fileKey);
    try {
      await accidentController.deleteFile(accessToken, fileItem._id, fileKey);
      toast.success(t("file_deleted"));
      setFileItem((prev) => ({ ...prev, files: prev.files.filter((f) => f.key !== fileKey) }));
      onReload();
    } catch { toast.error(t("error_deleting")); }
    finally { setDeletingFile(null); }
  };

  const handleViewFile = async (fileKey, fileName) => {
    try {
      const blobUrl = await accidentController.getFile(accessToken, fileKey);
      if (!blobUrl) { toast.error(t("error_downloading_file")); return; }
      const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName || "");
      if (isImage) {
        window.open(blobUrl, "_blank");
      } else {
        window.open(blobUrl, "_blank");
      }
    } catch { toast.error(t("error_downloading_file")); }
  };

  const SECTIONS = [
    { key: "establishment",       label: t("establishment") },
    { key: "employee",            label: t("employee") },
    { key: "location_moment",     label: t("location_moment") },
    { key: "description",         label: t("description") },
    { key: "case_classification", label: t("classification") },
    { key: "impact",              label: t("impact") },
    { key: "followup",            label: t("status") },
    { key: "rootcause",           label: t("root_cause") },
    { key: "capa",                label: "CAPA" },
    { key: "closure",             label: t("closure") },
  ];

  const siteCountry = sites.find((s) => s._id === (editingData.site || contextSite))?.country || "";
  const filteredAccidentTypes = siteCountry
    ? accidentTypes.filter((a) => !a.country || a.country === siteCountry)
    : accidentTypes;

  const renderFormModal = () => {
    const editingItem = editingId && editingId !== "new"
      ? items?.find((i) => i._id === editingId)
      : null;
    const accId    = editingItem ? formatAccidentId(editingItem) : "";
    const isOpen   = showModal && !!editingId && editingId !== "new";

    return (
      <>
        <div
          className={`at-drawer__backdrop${isOpen ? " at-drawer__backdrop--open" : ""}`}
          onClick={cancelEdit}
        />
        <div className={`at-drawer__panel${isOpen ? " at-drawer__panel--open" : ""}`}>
          {/* Header */}
          <div className="at-drawer__header">
            <div className="at-drawer__header-left">
              {accId && <span className="at-drawer__id-pill">{accId}</span>}
              <div>
                <div className="at-drawer__title">{t("edit_accident")}</div>
                {editingItem?.date && (
                  <div className="at-drawer__subtitle">{formatDate(editingItem.date)}</div>
                )}
              </div>
            </div>
            <button className="at-drawer__close-btn" onClick={cancelEdit} type="button">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* Body — section tabs */}
          <div className="at-drawer__body">
            {isOpen && renderSectionPanel()}
          </div>

          {/* Footer */}
          <div className="at-drawer__footer">
            <Button primary loading={saving} disabled={saving} onClick={handleSave}>
              <Icon name="save" /> {t("save")}
            </Button>
            <button className="at-drawer__cancel-btn" onClick={cancelEdit} disabled={saving} type="button">
              {t("cancel")}
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderQuickBar = () => (
    <div className="at-table__quick-bar">
      <span className="at-table__quick-bar-label">{t("new_accident")}</span>
      <div className="at-table__quick-fields">
        {/* División */}
        <div className="at-table__quick-field">
          <label>{t("division")}</label>
          <FilterDropdown
            options={divisions}
            value={editingData.division}
            onChange={(v) => { if (v === "__add_division__") setAddCatalog("division"); else setField("division", v); }}
            placeholder={t("division")}
            {...(canManageCatalogs ? { addLabel: `+ ${t("division")}`, addValue: "__add_division__" } : {})}
          />
        </div>
        {/* Forma del accidente */}
        <div className="at-table__quick-field">
          <label>{t("accident_mechanism")}</label>
          <FilterDropdown
            options={accidentMechOptions}
            value={editingData.accidentMechanism}
            onChange={(v) => { if (v === "__add_mechanism__") setAddCatalog("mechanism"); else setField("accidentMechanism", v); }}
            placeholder={t("accident_mechanism")}
            addLabel={`+ ${t("accident_mechanism")}`}
            addValue="__add_mechanism__"
          />
        </div>
        {/* Sector */}
        <div className="at-table__quick-field">
          <label>{t("sector")}</label>
          <FilterDropdown
            options={sectors}
            value={editingData.sector}
            onChange={(v) => { if (v === "__add_sector__") setAddCatalog("sector"); else setField("sector", v); }}
            placeholder={t("sector")}
            {...(canManageCatalogs ? { addLabel: `+ ${t("sector")}`, addValue: "__add_sector__" } : {})}
          />
        </div>
        {/* Lugar */}
        <div className="at-table__quick-field">
          <label>{t("exact_location")}</label>
          <input type="text" value={editingData.location} onChange={(e) => setField("location", e.target.value)}
            placeholder={t("exact_location")} className="at-table__quick-input" />
        </div>
        {/* Empleado */}
        <div className="at-table__quick-field">
          <label>{t("employee")}</label>
          <input
            type="text"
            value={editingData.employee}
            onChange={(e) => setField("employee", e.target.value)}
            placeholder={t("employee")}
            className="at-table__quick-input"
            readOnly={employeeStatus === "found"}
            style={employeeStatus === "found" ? { background: "#f5f5f5", color: "#374151" } : {}}
          />
        </div>
        {/* Legajo */}
        <div className="at-table__quick-field">
          <label>
            {t("legajo")}
            {employeeStatus === "found" && <span className="at-table__quick-emp-ok"> ✓</span>}
          </label>
          <input type="text" value={editingData.legajo} onChange={(e) => handleLegajoChange(e.target.value)}
            placeholder={t("legajo")} className="at-table__quick-input" />
        </div>
        {/* Fecha */}
        <div className="at-table__quick-field">
          <label>{t("date")}</label>
          <input type="date" value={editingData.date} onChange={(e) => setField("date", e.target.value)}
            className="at-table__quick-input" />
        </div>
        {/* Hora */}
        <div className="at-table__quick-field">
          <label>{t("time_hs")}</label>
          <input type="time" value={editingData.time} onChange={(e) => setField("time", e.target.value)}
            className="at-table__quick-input" />
        </div>
      </div>
      <div className="at-table__quick-actions">
        <Popup size="mini" content={t("save_and_complete")} trigger={
          <Button icon size="mini" loading={saving} disabled={saving} onClick={handleSave} className="at-table__save-btn">
            <Icon name="check" />
          </Button>
        } />
        <Popup size="mini" content={t("cancel")} trigger={
          <Button icon size="mini" onClick={cancelEdit} disabled={saving}>
            <Icon name="close" />
          </Button>
        } />
      </div>
    </div>
  );

  const renderEditRow = () => (
    <Table.Row key="editing" positive={editingId === "new"} warning={editingId !== "new"}>
      {/* 1. ID */}
      <Table.Cell style={{ fontSize: "0.8em", color: "#888", whiteSpace: "nowrap" }}>
        {editingId === "new"
          ? "Nuevo"
          : formatAccidentId({ date: editingData.date, customId: items?.find((i) => i._id === editingId)?.customId })}
      </Table.Cell>
      {/* 2. Fecha */}
      <Table.Cell>
        <input type="date" value={editingData.date} onChange={(e) => setField("date", e.target.value)}
          className="at-table__inline-input" />
      </Table.Cell>
      {/* 3. Sitio — read-only */}
      <Table.Cell style={{ fontSize: "0.85em", color: "#555" }}>
        {sites.find((s) => s._id === (editingData.site || contextSite))?.name || "-"}
      </Table.Cell>
      {/* 4. División */}
      <Table.Cell>
        <InlineSelect
          options={divisions}
          value={editingData.division}
          onChange={(v) => setField("division", v)}
          placeholder={t("division")}
        />
      </Table.Cell>
      {/* 5. Tipo (caseType) */}
      <Table.Cell>
        <InlineSelect
          options={CASE_TYPE_OPTIONS.map(o => ({...o, text: t(o.value)}))}
          value={editingData.caseType}
          onChange={(v) => setField("caseType", v)}
          placeholder={t("case_type") || "Tipo"}
        />
      </Table.Cell>
      {/* 6. Forma (accidentMechanism) */}
      <Table.Cell>
        <InlineSelect
          options={accidentMechOptions}
          value={editingData.accidentMechanism}
          onChange={(v) => {
            if (v === "__add_mechanism__") { setAddCatalog("mechanism"); }
            else setField("accidentMechanism", v);
          }}
          placeholder={t("accident_mechanism")}
          addLabel={`+ ${t("accident_mechanism")}`}
          addValue="__add_mechanism__"
        />
      </Table.Cell>
      {/* 7. Parte — handled in section panel */}
      <Table.Cell style={{ fontSize: "0.8em", color: "#aaa" }}>-</Table.Cell>
      {/* 8. Empleado */}
      <Table.Cell>
        <input type="text" value={editingData.employee} onChange={(e) => setField("employee", e.target.value)}
          placeholder={t("employee")} className="at-table__inline-input" />
      </Table.Cell>
      {/* 9. Días */}
      <Table.Cell>
        <input type="number" min="0" value={editingData.lostDays} onChange={(e) => setField("lostDays", Number(e.target.value))}
          className="at-table__inline-input" style={{ width: "60px" }} />
      </Table.Cell>
      {/* 10. Estado */}
      <Table.Cell>
        <InlineSelect
          options={CASE_STATUS_OPTIONS.map(o => ({...o, text: t(o.value)}))}
          value={editingData.caseStatus}
          onChange={(v) => setField("caseStatus", v)}
          placeholder="Estado"
        />
      </Table.Cell>
      {/* 11. Acciones — Guardar / Cancelar */}
      <Table.Cell textAlign="center" className="at-table__actions">
        <Popup size="mini" content={t("save")} trigger={
          <Button icon size="mini" loading={saving} disabled={saving} onClick={handleSave} className="at-table__save-btn">
            <Icon name="save" />
          </Button>
        } />
        <Popup size="mini" content={t("cancel")} trigger={
          <Button icon size="mini" onClick={cancelEdit} disabled={saving}>
            <Icon name="close" />
          </Button>
        } />
      </Table.Cell>
    </Table.Row>
  );

  const renderSectionPanel = () => {
    if (!activeSection || !editingId) return null;

    const panelContent = {
      establishment: (
        <div className="at-table__section-fields">

          {/* ── EMPLEADOR ── */}
          <CollapseSection title={t("employer")}>
            <div className="at-table__field-grid">
              <div className={"at-table__field-row" + errClass("employerName")}>
                <label>{t("legal_name")} <span style={{ color: "#DC2626" }}>*</span></label>
                <input type="text" value={editingData.employerName}
                  onChange={(e) => setField("employerName", e.target.value)}
                  placeholder="Nombre legal de la empresa"
                  className="at-table__inline-input" />
              </div>
              <div className="at-table__field-row">
                <label>{t("tax_id")}</label>
                <input type="text" value={editingData.cuit}
                  onChange={(e) => setField("cuit", e.target.value)}
                  placeholder="XX-XXXXXXXX-X"
                  className="at-table__inline-input" />
              </div>
              <div className="at-table__field-row">
                <label>{t("legal_address")}</label>
                <input type="text" value={editingData.legalAddress}
                  onChange={(e) => setField("legalAddress", e.target.value)}
                  placeholder="Calle, número, ciudad"
                  className="at-table__inline-input" />
              </div>
              <div className="at-table__field-row">
                <label>{t("phone")}</label>
                <input type="tel" value={editingData.employerPhone}
                  onChange={(e) => setField("employerPhone", e.target.value)}
                  placeholder="+54 11 XXXX-XXXX"
                  className="at-table__inline-input" />
              </div>
            </div>
          </CollapseSection>

          {/* ── ESTABLECIMIENTO ── */}
          <CollapseSection title={t("establishment_where")} mt>
            <div className="at-table__field-grid">
              <div className={"at-table__field-row" + errClass("establishmentName")}>
                <label>{t("establishment_name")} <span style={{ color: "#DC2626" }}>*</span></label>
                <input type="text" value={editingData.establishmentName}
                  onChange={(e) => setField("establishmentName", e.target.value)}
                  placeholder="Ej: Centro de distribución Pilar"
                  className="at-table__inline-input" />
              </div>
              <div className="at-table__field-row">
                <label>{t("establishment_address")}</label>
                <input type="text" value={editingData.establishmentAddress}
                  onChange={(e) => setField("establishmentAddress", e.target.value)}
                  placeholder="Dirección física del sitio"
                  className="at-table__inline-input" />
              </div>
              <div className="at-table__field-row">
                <label>{t("economic_activity")} (CIIU Rev.4)</label>
                <InlineCiiuSelect
                  options={ciiuOptions}
                  value={editingData.economicActivity}
                  onChange={(v) => setField("economicActivity", v)}
                  canAdd={canCreateCiiu}
                  onAdd={() => setAddCatalog("ciiu")}
                />
              </div>
              <div className={"at-table__field-row" + errClass("establishmentSize")}>
                <label>{t("establishment_size")} <span style={{ color: "#DC2626" }}>*</span></label>
                <InlineSelect
                  options={ESTABLISHMENT_SIZE_OPTIONS.map(o => ({...o, text: t(o.value)}))}
                  value={editingData.establishmentSize}
                  onChange={(v) => setField("establishmentSize", v)}
                  placeholder={t("select")}
                />
              </div>
            </div>
          </CollapseSection>

          {/* ── LOCALIZACIÓN INTERNA ── */}
          <CollapseSection title={t("internal_location")} mt>
            <div className="at-table__field-grid">
              <div className="at-table__field-row">
                <label>{t("division")}</label>
                <InlineSelect
                  options={divisions}
                  value={editingData.division}
                  onChange={(v) => { if (v === "__add_div__") setAddCatalog("division"); else setField("division", v); }}
                  placeholder={t("division")}
                  addLabel={canCreateCiiu ? "+ Nueva división" : undefined}
                  addValue="__add_div__"
                />
              </div>
              <div className="at-table__field-row">
                <label>{t("site")}</label>
                <InlineSelect
                  options={sites.map((s) => ({ key: s._id, value: s._id, text: s.name }))}
                  value={editingData.site}
                  onChange={(v) => setField("site", v)}
                  placeholder={t("select")}
                />
              </div>
              <div className="at-table__field-row">
                <label>{t("sector")}</label>
                <InlineSelect
                  options={sectors}
                  value={editingData.sector}
                  onChange={(v) => { if (v === "__add_sec__") setAddCatalog("sector"); else setField("sector", v); }}
                  placeholder={t("select")}
                  addLabel={canCreateCiiu ? "+ Nuevo sector" : undefined}
                  addValue="__add_sec__"
                />
              </div>
              <div className="at-table__field-row">
                <label>{t("exact_location")}</label>
                <input type="text" value={editingData.location}
                  onChange={(e) => setField("location", e.target.value)}
                  placeholder="Pasillo 3, estiba alta, máquina..."
                  className="at-table__inline-input" />
              </div>
            </div>
          </CollapseSection>
        </div>
      ),
      employee: (
        <div className="at-table__section-fields">

          {/* ── IDENTIFICACIÓN ── */}
          <CollapseSection title={t("identification")}>
          <div className="at-table__field-grid">
            {/* Legajo */}
            <div className={"at-table__field-row" + errClass("legajo")}>
              <label>{t("legajo")} <span style={{ color: "#DC2626" }}>*</span></label>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <input type="text" value={editingData.legajo}
                  onChange={(e) => handleLegajoChange(e.target.value)}
                  className="at-table__inline-input" placeholder="Nº legajo" />
                {employeeStatus === "found"     && <Label size="tiny" color="green"><Icon name="check" />{t("found")}</Label>}
                {employeeStatus === "not_found" && <Label size="tiny" color="orange"><Icon name="warning sign" />{t("not_found")}</Label>}
              </div>
            </div>
            {/* Nombre */}
            <div className={"at-table__field-row" + errClass("empNombre")}>
              <label>{t("first_name")} <span style={{ color: "#DC2626" }}>*</span></label>
              <input type="text" value={empNombre}
                onChange={(e) => handleEmpNombreChange(e.target.value)}
                className="at-table__inline-input" placeholder={t("first_name")}
                readOnly={employeeStatus === "found"}
                style={employeeStatus === "found" ? { background: "#f5f5f5" } : {}} />
            </div>
            {/* Apellido */}
            <div className={"at-table__field-row" + errClass("empApellido")}>
              <label>{t("last_name")} <span style={{ color: "#DC2626" }}>*</span></label>
              <input type="text" value={empApellido}
                onChange={(e) => handleEmpApellidoChange(e.target.value)}
                className="at-table__inline-input" placeholder={t("last_name")}
                readOnly={employeeStatus === "found"}
                style={employeeStatus === "found" ? { background: "#f5f5f5" } : {}} />
            </div>
            {/* Documento */}
            <div className="at-table__field-row">
              <label>{t("id_document")}</label>
              <input type="text" value={editingData.documento}
                onChange={(e) => setField("documento", e.target.value)}
                className="at-table__inline-input" placeholder="DNI / CI / Pasaporte" />
            </div>
            {/* Sexo */}
            <div className="at-table__field-row">
              <label>{t("gender")}</label>
              <InlineSelect options={GENDER_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.gender}
                onChange={(v) => setField("gender", v)} placeholder={t("select")} />
            </div>
            {/* Fecha de nacimiento */}
            <div className="at-table__field-row">
              <label>{t("birth_date")}</label>
              <input type="date" value={editingData.birthDate}
                onChange={(e) => setField("birthDate", e.target.value)}
                className="at-table__inline-input" />
            </div>
            {/* Edad calculada */}
            <div className="at-table__field-row">
              <label>{t("age")}</label>
              <input type="text" readOnly
                value={editingData.birthDate
                  ? (() => {
                      const b = new Date(editingData.birthDate);
                      const now = new Date();
                      let age = now.getFullYear() - b.getFullYear();
                      if (now.getMonth() < b.getMonth() || (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())) age--;
                      return `${age} años`;
                    })()
                  : "—"}
                className="at-table__inline-input"
                style={{ background: "#f5f5f5", color: "#6B7280" }} />
            </div>
            {/* Dirección particular */}
            <div className="at-table__field-row">
              <label>{t("personal_address")}</label>
              <input type="text" value={editingData.employeeAddress}
                onChange={(e) => setField("employeeAddress", e.target.value)}
                className="at-table__inline-input" placeholder="Domicilio del empleado" />
            </div>
          </div>

          {/* Botón registrar eventual */}
          {employeeStatus === "not_found" && empNombre && empApellido && (
            <div style={{ marginTop: "0.5rem" }}>
              <Button size="mini" color="teal" loading={registeringEmp}
                onClick={handleRegisterEmployee} type="button">
                <Icon name="user plus" />{t("register_in_records")}
              </Button>
            </div>
          )}
          </CollapseSection>

          {/* ── VÍNCULO LABORAL ── */}
          <CollapseSection title={t("employment_relationship")} mt>
          <div className="at-table__field-grid">
            <div className="at-table__field-row">
              <label>{t("contract_type")}</label>
              <InlineSelect options={CONTRACT_TYPE_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.contractType}
                onChange={(v) => setField("contractType", v)} placeholder={t("select")} />
            </div>
            <div className={"at-table__field-row" + errClass("contractualLink")}>
              <label>{t("contractual_link")} <span style={{ color: "#DC2626" }}>*</span></label>
              <InlineSelect options={CONTRACTUAL_LINK_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.contractualLink}
                onChange={(v) => setField("contractualLink", v)} placeholder={t("select")} />
            </div>
            <div className={"at-table__field-row" + errClass("occupation")}>
              <label>{t("occupation")} <span style={{ color: "#DC2626" }}>*</span></label>
              <InlineSelect options={ocupaciones} value={editingData.occupation}
                onChange={(v) => { if (v === "__add_ocup__") setAddCatalog("ocupacion"); else setField("occupation", v); }}
                placeholder={t("select")}
                addLabel={canCreateCiiu ? "+ Nueva ocupación" : undefined}
                addValue="__add_ocup__" />
            </div>
            <div className="at-table__field-row">
              <label>{t("seniority")}</label>
              <InlineSelect options={POSITION_TENURE_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.seniority}
                onChange={(v) => setField("seniority", v)} placeholder={t("select")} />
            </div>
          </div>
          </CollapseSection>
        </div>
      ),
      location_moment: (() => {
        const LANG_LOCALE = { es: "es-AR", en: "en-US", pt: "pt-BR", fr: "fr-FR" };
        const weekday = editingData.date
          ? new Date(editingData.date + "T12:00:00").toLocaleDateString(LANG_LOCALE[language] || "es-AR", { weekday: "long" })
          : "—";
        const hoursAuto = (() => {
          if (!editingData.time || !editingData.workdayStartTime) return "";
          const [ah, am] = editingData.time.split(":").map(Number);
          const [sh, sm] = editingData.workdayStartTime.split(":").map(Number);
          const diff = (ah * 60 + am - sh * 60 - sm) / 60;
          return diff >= 0 && diff <= 24 ? String(Math.round(diff * 2) / 2) : "";
        })();
        return (
          <div className="at-table__section-fields">
            <CollapseSection title={t("accident_location")}>
            <div className="at-table__field-grid">
              <div className={"at-table__field-row" + errClass("locationType")}>
                <label>{t("location_type")} <span style={{ color: "#DC2626" }}>*</span></label>
                <InlineSelect options={LOCATION_TYPE_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.locationType}
                  onChange={(v) => setField("locationType", v)} placeholder={t("select")} />
              </div>
              <div className="at-table__field-row">
                <label>{t("work_environment")}</label>
                <InlineSelect
                  options={workEnvOptions}
                  value={editingData.workEnvironment}
                  onChange={(v) => { if (v === "__add_wenv__") setAddCatalog("workEnv"); else setField("workEnvironment", v); }}
                  placeholder={t("select")}
                  addLabel="+ Agregar nuevo..."
                  addValue="__add_wenv__"
                />
              </div>
            </div>

            {editingData.isCommute === "yes" && (
              <div className="at-table__field-row" style={{ marginTop: "0.5rem" }}>
                <label style={{ color: "#B45309", fontWeight: 600 }}>
                  ⚠ Accidente in itinere — no suma a las tasas de la empresa
                </label>
              </div>
            )}
            </CollapseSection>

            <CollapseSection title={t("date_time_shift")} mt>
            <div className="at-table__field-grid">
              <div className={"at-table__field-row" + errClass("date")}>
                <label>{t("accident_date")} <span style={{ color: "#DC2626" }}>*</span></label>
                <input type="date" value={editingData.date}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setField("date", e.target.value)}
                  className="at-table__inline-input" />
              </div>
              <div className={"at-table__field-row" + errClass("time")}>
                <label>{t("accident_time")} <span style={{ color: "#DC2626" }}>*</span></label>
                <input type="time" value={editingData.time}
                  onChange={(e) => setField("time", e.target.value)}
                  className="at-table__inline-input" />
              </div>
              <div className="at-table__field-row">
                <label>{t("day_of_week")}</label>
                <input type="text" value={weekday} readOnly
                  className="at-table__inline-input" style={{ background: "#f5f5f5", color: "#888" }} />
              </div>
              <div className="at-table__field-row">
                <label>{t("shift")}</label>
                <InlineSelect options={SHIFT_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.shift}
                  onChange={(v) => setField("shift", v)} placeholder={t("select")} />
              </div>
              <div className="at-table__field-row">
                <label>{t("workday_start_time")}</label>
                <input type="time" value={editingData.workdayStartTime}
                  onChange={(e) => setField("workdayStartTime", e.target.value)}
                  className="at-table__inline-input" />
              </div>
              <div className="at-table__field-row">
                <label>{t("hours_worked_before")}</label>
                <input type="number" min="0" max="24" step="0.5"
                  value={editingData.hoursWorkedBeforeAccident !== "" ? editingData.hoursWorkedBeforeAccident : hoursAuto}
                  onChange={(e) => setField("hoursWorkedBeforeAccident", e.target.value)}
                  className="at-table__inline-input" placeholder="Auto si hay hora inicio" />
              </div>
              <div className="at-table__field-row">
                <label>{t("routine_task")}</label>
                <InlineSelect options={YES_NO_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.isRoutineTask}
                  onChange={(v) => setField("isRoutineTask", v)} placeholder={t("select")} />
              </div>
              <div className="at-table__field-row">
                <label>{t("is_commute")}</label>
                <InlineSelect options={[
                  { key: "no",  value: "no",  text: t("no")  },
                  { key: "yes", value: "yes", text: t("yes_commute") },
                ]} value={editingData.isCommute}
                  onChange={(v) => setField("isCommute", v)} placeholder={t("select")} />
              </div>
            </div>
            </CollapseSection>
          </div>
        );
      })(),
      description: (() => {
        const OtherSelect = ({ options, field, otherField, label, onAdd }) => {
          const selectedOpt = options.find((o) => o.value === editingData[field]);
          const isOther = selectedOpt?.text?.toLowerCase().startsWith("otro");
          return (
            <div className={"at-table__field-row" + errClass(field)}>
              <label>{label}</label>
              <div>
                <InlineSelect
                  options={options}
                  value={editingData[field]}
                  onChange={(v) => {
                    if (v === "__add__") { onAdd && onAdd(); return; }
                    setField(field, v);
                    if (!options.find((o) => o.value === v)?.text?.toLowerCase().startsWith("otro")) setField(otherField, "");
                  }}
                  placeholder="Seleccionar"
                  addLabel={onAdd ? "+ Agregar nuevo..." : undefined}
                  addValue={onAdd ? "__add__" : undefined}
                />
                {isOther && (
                  <input type="text" value={editingData[otherField]}
                    onChange={(e) => setField(otherField, e.target.value)}
                    className="at-table__inline-input" style={{ marginTop: "0.3rem" }}
                    placeholder="Especificar..." />
                )}
              </div>
            </div>
          );
        };
        return (
          <div className="at-table__section-fields">

            {/* ── RELATO ── */}
            <CollapseSection title={t("account")}>
            <div className={"at-table__field-row" + errClass("briefDescription")}>
              <label>{t("brief_description")} <span style={{ color: "#DC2626" }}>*</span> <span style={{ fontWeight: 400, color: "#888", fontSize: "0.78rem" }}>(mín. 30 caracteres)</span></label>
              <textarea value={editingData.briefDescription}
                onChange={(e) => setField("briefDescription", e.target.value)}
                className="at-table__inline-textarea" rows={3}
                placeholder="2-3 líneas que resumen qué pasó. La narrativa completa va en Causa Raíz." />
              {editingData.briefDescription.length > 0 && editingData.briefDescription.length < 30 && (
                <span style={{ color: "#B45309", fontSize: "0.75rem" }}>
                  {editingData.briefDescription.length}/30 caracteres mínimos
                </span>
              )}
            </div>

            </CollapseSection>

            {/* ── FORMA Y AGENTES ── */}
            <CollapseSection title={t("mechanism_agents")} mt>
            <div className="at-table__field-grid">
              <OtherSelect options={accidentMechOptions}  field="accidentMechanism"   otherField="accidentMechanismOther"      label={<>{t("accident_mechanism")} <span style={{ color: "#DC2626" }}>*</span></>}             onAdd={() => setAddCatalog("mechanism")} />
              <OtherSelect options={deviationOptions}     field="deviation"           otherField="deviationOther"              label={t("deviation")}         onAdd={() => setAddCatalog("deviation")} />
              <OtherSelect options={materialAgentOptions} field="materialAgentCause"  otherField="materialAgentCauseOther"     label={<>{t("material_agent_cause")} <span style={{ color: "#DC2626" }}>*</span></>} onAdd={() => setAddCatalog("materialAgent")} />
              <OtherSelect options={materialAgentOptions} field="materialAgentDeviation" otherField="materialAgentDeviationOther" label={t("material_agent_deviation")} onAdd={() => setAddCatalog("materialAgent")} />
            </div>

            </CollapseSection>

            {/* ── PROCESO Y ACTIVIDAD ── */}
            <CollapseSection title={t("process_activity")} mt>
            <div className="at-table__field-grid">
              <OtherSelect options={workProcessOptions} field="workProcess" otherField="workProcessOther" label={<>{t("work_process")} <span style={{ color: "#DC2626" }}>*</span></>} onAdd={() => setAddCatalog("workProcess")} />
              <div className="at-table__field-row">
                <label>{t("specific_activity")}</label>
                <input type="text" value={editingData.specificActivity}
                  onChange={(e) => setField("specificActivity", e.target.value)}
                  className="at-table__inline-input" placeholder="Ej: Bajaba cajas desde estiba alta con escalera de mano" />
              </div>
              <div className="at-table__field-row">
                <label>{t("objects_used")}</label>
                <input type="text" value={editingData.objectsUsed}
                  onChange={(e) => setField("objectsUsed", e.target.value)}
                  className="at-table__inline-input" placeholder="Lo que tenía en sus manos al momento" />
              </div>
              <div className={"at-table__field-row" + errClass("classification")}>
                <label>{t("classification")} <span style={{ color: "#DC2626" }}>*</span></label>
                <InlineSelect options={CLASSIFICATION_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.classification}
                  onChange={(v) => setField("classification", v)} placeholder={t("select")} />
              </div>
            </div>

            </CollapseSection>

            {/* ── ATENCIÓN INMEDIATA ── */}
            <CollapseSection title={t("immediate_care")} mt>
            <div className="at-table__field-grid">
              <div className="at-table__field-row">
                <label>{t("first_aid")}</label>
                <InlineSelect options={YES_NO_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.firstAid}
                  onChange={(v) => setField("firstAid", v)} placeholder={t("select")} />
              </div>
              <div className="at-table__field-row">
                <label>{t("referral")}</label>
                <input type="text" value={editingData.referral}
                  onChange={(e) => setField("referral", e.target.value)}
                  className="at-table__inline-input" placeholder="Nombre de la clínica/hospital" />
              </div>
              <div className="at-table__field-row">
                <label>{t("art_insurance")}</label>
                <input type="text" value={editingData.artInsurance}
                  onChange={(e) => setField("artInsurance", e.target.value)}
                  className="at-table__inline-input" placeholder="Nombre de la aseguradora" />
              </div>
              <div className="at-table__field-row">
                <label>{t("claim_number")}</label>
                <input type="text" value={editingData.claimNumber}
                  onChange={(e) => setField("claimNumber", e.target.value)}
                  className="at-table__inline-input" placeholder="Identificador ante la ART" />
              </div>
            </div>
            </CollapseSection>
          </div>
        );
      })(),
      case_classification: (() => {
        const autoLostDays = (() => {
          if (!editingData.sickLeaveDate || !editingData.returnToWorkDate) return null;
          const diff = (new Date(editingData.returnToWorkDate) - new Date(editingData.sickLeaveDate)) / 86400000;
          return diff >= 0 ? Math.round(diff) : null;
        })();
        return (
          <div className="at-table__section-fields">

            {/* ── TIPO DE CASO ── */}
            <CollapseSection title={t("case_type")}>
            <div className="at-table__field-grid">
              <div className={"at-table__field-row" + errClass("caseType")}>
                <label>{t("case_type")} <span style={{ color: "#DC2626" }}>*</span></label>
                <InlineSelect options={CASE_TYPE_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.caseType}
                  onChange={(v) => setField("caseType", v)} placeholder={t("select")} />
              </div>
              <div className="at-table__field-row">
                <label>{t("severity")}</label>
                <InlineSelect options={SEVERITY_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.initialSeverity}
                  onChange={(v) => setField("initialSeverity", v)} placeholder={t("select")} />
              </div>
              <div className="at-table__field-row">
                <label>{t("generated_lost_days")}</label>
                <InlineSelect options={YES_NO_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.generatedLostDays}
                  onChange={(v) => setField("generatedLostDays", v)} placeholder={t("select")} />
              </div>
              <div className="at-table__field-row">
                <label>{t("was_fatal")}</label>
                <InlineSelect options={[
                  { key: "no",  value: "no",  text: t("no")          },
                  { key: "yes", value: "yes", text: t("yes_fatal")   },
                ]} value={editingData.isFatal}
                  onChange={(v) => { setField("isFatal", v); if (v !== "yes") setField("fatalityDate", ""); }}
                  placeholder={t("select")} />
              </div>
            </div>

            </CollapseSection>

            {/* ── CÓMPUTO DE DÍAS ── */}
            <CollapseSection title={t("days_computation")} mt>
            <div className="at-table__field-grid">
              <div className="at-table__field-row">
                <label>{t("sick_leave_date")}</label>
                <input type="date" value={editingData.sickLeaveDate}
                  onChange={(e) => setField("sickLeaveDate", e.target.value)}
                  className="at-table__inline-input" />
              </div>
              <div className="at-table__field-row">
                <label>{t("medical_discharge")}</label>
                <input type="date" value={editingData.medicalDischargeDate}
                  onChange={(e) => setField("medicalDischargeDate", e.target.value)}
                  className="at-table__inline-input" />
              </div>
              <div className="at-table__field-row">
                <label>{t("return_to_work")}</label>
                <input type="date" value={editingData.returnToWorkDate}
                  onChange={(e) => setField("returnToWorkDate", e.target.value)}
                  className="at-table__inline-input" />
              </div>
              <div className="at-table__field-row">
                <label>
                  {t("lost_days")} <span style={{ color: "#DC2626" }}>*</span>
                  {autoLostDays !== null && editingData.lostDays === 0 && (
                    <button type="button" onClick={() => setField("lostDays", autoLostDays)}
                      style={{ marginLeft: "0.5rem", fontSize: "0.7rem", color: "#3F7BC6", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                      ↺ auto ({autoLostDays}d)
                    </button>
                  )}
                </label>
                <input type="number" min={0} max={365} value={editingData.lostDays}
                  onChange={(e) => setField("lostDays", Number(e.target.value))}
                  className="at-table__inline-input" style={{ width: "100px" }} />
              </div>
              <div className="at-table__field-row">
                <label>{t("restriction_days")}</label>
                <input type="number" min={0} max={365}
                  value={editingData.restrictionDays === "" ? "" : editingData.restrictionDays}
                  onChange={(e) => setField("restrictionDays", e.target.value === "" ? "" : Number(e.target.value))}
                  className="at-table__inline-input" style={{ width: "100px" }} />
              </div>
              <div className="at-table__field-row">
                <label>{t("loss_of_consciousness")}</label>
                <InlineSelect options={YES_NO_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.lostConsciousness}
                  onChange={(v) => setField("lostConsciousness", v)} placeholder={t("select")} />
              </div>
              <div className="at-table__field-row">
                <label>{t("medical_assistance")}</label>
                <InlineSelect options={MEDICAL_ASSISTANCE_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.medicalAssistance}
                  onChange={(v) => setField("medicalAssistance", v)} placeholder={t("select")} />
              </div>
              <div className="at-table__field-row" style={editingData.isFatal !== "yes" ? { opacity: 0.4, pointerEvents: "none" } : {}}>
                <label>{t("fatality_date")} <span style={{ fontWeight: 400, color: "#888", fontSize: "0.78rem" }}>(si aplica)</span></label>
                <input type="date" value={editingData.fatalityDate}
                  onChange={(e) => setField("fatalityDate", e.target.value)}
                  className="at-table__inline-input"
                  disabled={editingData.isFatal !== "yes"} />
              </div>
            </div>
            </CollapseSection>
          </div>
        );
      })(),
      impact: (
        <div className="at-table__section-fields">

          {/* ── NATURALEZA DE LA LESIÓN ── */}
          <CollapseSection title={t("injury_nature_section")}>
          <div className="at-table__field-grid">
            <div className={"at-table__field-row" + errClass("injuryType")}>
              <label>{t("nature")} <span style={{ color: "#DC2626" }}>*</span></label>
              <InlineSelect options={injuryNatureOptions} value={editingData.injuryType}
                onChange={(v) => { if (v === "__add_injury__") { setAddCatalog("injuryNature"); } else setField("injuryType", v); }}
                placeholder={t("select")} addLabel="+ Agregar nuevo..." addValue="__add_injury__" />
            </div>
            <div className="at-table__field-row">
              <label>{t("clinical_severity")}</label>
              <InlineSelect options={CLINICAL_SEVERITY_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.clinicalSeverity}
                onChange={(v) => setField("clinicalSeverity", v)} placeholder={t("select")} />
            </div>
            <div className="at-table__field-row">
              <label>{t("laterality")}</label>
              <InlineSelect options={LATERALITY_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.laterality}
                onChange={(v) => setField("laterality", v)} placeholder={t("select")} />
            </div>
            <div className="at-table__field-row">
              <label>{t("medical_diagnosis")}</label>
              <input type="text" value={editingData.medicalDiagnosis}
                onChange={(e) => setField("medicalDiagnosis", e.target.value)}
                className="at-table__inline-input"
                placeholder="Ej: Esguince grado II tobillo derecho" />
            </div>
          </div>
          </CollapseSection>

          {/* ── BODY MAP ── */}
          <CollapseSection title={<>{t("body_parts_affected")} <span style={{ fontWeight: 400, color: "#91A8C8", fontSize: "0.75rem" }}>({editingData.bodyParts.length} {t("selected_count")})</span></>} mt>
          <div className="at-table__impact-row">
            <div className="at-table__body-checklist">
              {BODY_PART_GROUPS.map((group) => (
                <div key={group.label} className="at-table__body-group">
                  <div className="at-table__body-group-label">{group.label}</div>
                  <div className="at-table__body-group-items">
                    {group.parts.map((part) => {
                      const checked = editingData.bodyParts.includes(part);
                      return (
                        <label key={part} className={`at-table__body-check-item${checked ? " selected" : ""}`}>
                          <input type="checkbox" checked={checked}
                            onChange={() => {
                              const cur = editingData.bodyParts;
                              setField("bodyParts", checked ? cur.filter((p) => p !== part) : [...cur, part]);
                            }}
                          />
                          {part}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <BodyDiagram
              selectedParts={
                Object.entries(DIAGRAM_PART_MAP)
                  .filter(([, parts]) => parts.some((p) => editingData.bodyParts.includes(p)))
                  .map(([zone]) => zone)
              }
              onToggle={(zone) => {
                const assoc = DIAGRAM_PART_MAP[zone] || [zone];
                const cur = editingData.bodyParts;
                const allSelected = assoc.every((p) => cur.includes(p));
                setField(
                  "bodyParts",
                  allSelected
                    ? cur.filter((p) => !assoc.includes(p))
                    : [...cur, ...assoc.filter((p) => !cur.includes(p))],
                );
              }}
            />
          </div>
          </CollapseSection>
        </div>
      ),
      followup: (() => {
        const isClosed   = editingData.caseStatus === "closed";
        const isReopened = editingData.caseStatus === "reopened";
        const closureTime = (() => {
          if (!editingData.closureDate || !editingData.reportReceptionDate) return null;
          const days = Math.round(
            (new Date(editingData.closureDate) - new Date(editingData.reportReceptionDate)) / 86400000
          );
          return days >= 0 ? `${days} día${days !== 1 ? "s" : ""} desde el reporte` : null;
        })();
        return (
          <div className="at-table__section-fields">
            <CollapseSection title={t("case_file_status")}>
            <div className="at-table__field-grid">
              <div className={"at-table__field-row" + errClass("caseStatus")}>
                <label>{t("case_status")} <span style={{ color: "#DC2626" }}>*</span></label>
                <InlineSelect options={CASE_STATUS_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.caseStatus}
                  onChange={(v) => setField("caseStatus", v)} placeholder={t("select")} />
              </div>
              <div className="at-table__field-row">
                <label>{t("report_reception_date")}</label>
                <input type="date" value={editingData.reportReceptionDate}
                  onChange={(e) => setField("reportReceptionDate", e.target.value)}
                  className="at-table__inline-input" />
              </div>
              <div className="at-table__field-row" style={!isClosed ? { opacity: 0.4, pointerEvents: "none" } : {}}>
                <label>
                  {t("closure_date")}
                  {closureTime && (
                    <span style={{ fontWeight: 400, color: "#2E9461", fontSize: "0.75rem", marginLeft: "0.5rem" }}>
                      ({closureTime})
                    </span>
                  )}
                </label>
                <input type="date" value={editingData.closureDate}
                  onChange={(e) => setField("closureDate", e.target.value)}
                  className="at-table__inline-input" disabled={!isClosed} />
              </div>
              <div className="at-table__field-row">
                <label>{t("notification_deadline")}</label>
                <span style={{ fontSize: "0.85rem", color: "#555", padding: "0.4rem 0" }}>
                  <strong>48 horas</strong> (Argentina)
                </span>
              </div>
            </div>

            </CollapseSection>

            {isReopened && (
              <CollapseSection title={t("reopening")} mt>
                <div className="at-table__field-grid">
                  <div className="at-table__field-row">
                    <label>{t("reopen_date")} <span style={{ fontWeight: 400, color: "#888", fontSize: "0.78rem" }}>(si aplica)</span></label>
                    <input type="date" value={editingData.reopenDate}
                      onChange={(e) => setField("reopenDate", e.target.value)}
                      className="at-table__inline-input" />
                  </div>
                  <div className="at-table__field-row">
                    <label>{t("reopen_reason")}</label>
                    <input type="text" value={editingData.reopenReason}
                      onChange={(e) => setField("reopenReason", e.target.value)}
                      className="at-table__inline-input"
                      placeholder="Nueva evidencia, recidiva clínica, fallo de acción correctiva..." />
                  </div>
                </div>
              </CollapseSection>
            )}
          </div>
        );
      })(),
      rootcause: (() => {
        const rc = editingData.rootCause;
        const meth = rc.methodology;
        const isFiveWhys  = meth === "5_whys";
        const isFishbone  = meth === "fishbone";
        const isCauseTree = meth === "cause_tree";
        const isBowtie    = meth === "bowtie";
        return (
          <div className="at-table__section-fields">
            {/* ── INVESTIGACIÓN · RESPONSABILIDAD ── */}
            <CollapseSection title={t("investigation_responsibility")}>
            <div className="at-table__field-grid">
              <div className={"at-table__field-row" + errClass("rc_responsible")}>
                <label>{t("responsible_person")} <span style={{ color: "#DC2626" }}>*</span></label>
                <input type="text" value={rc.responsiblePerson}
                  onChange={(e) => { setRootCause("responsiblePerson", e.target.value); if (e.target.value.trim()) setFormErrors((p) => { const n = {...p}; delete n.rc_responsible; return n; }); }}
                  className="at-table__inline-input" placeholder="Nombre y cargo" />
              </div>
              <div className="at-table__field-row">
                <label>{t("methodology")} <span style={{ color: "#DC2626" }}>*</span></label>
                <InlineSelect options={METHODOLOGY_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={rc.methodology}
                  onChange={(v) => setRootCause("methodology", v)} />
              </div>
              <div className="at-table__field-row">
                <label>{t("worker_rep_participated")}</label>
                <InlineSelect options={YES_NO_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={rc.workerRepParticipated}
                  onChange={(v) => setRootCause("workerRepParticipated", v)} />
              </div>
              {rc.workerRepParticipated === "yes" && (
                <div className="at-table__field-row">
                  <label>{t("worker_rep_name")}</label>
                  <input type="text" value={rc.workerRepName}
                    onChange={(e) => setRootCause("workerRepName", e.target.value)}
                    className="at-table__inline-input" />
                </div>
              )}
            </div>
            </CollapseSection>

            {/* ── RELATO Y CONTEXTO ── */}
            <CollapseSection title={t("narrative_context")} mt>
            <div className="at-table__field-row">
              <label>{t("accident_narrative")} <span style={{ color: "#DC2626" }}>*</span></label>
              <textarea value={rc.narrative}
                onChange={(e) => { narrativeManualRef.current = true; setRootCause("narrative", e.target.value); }}
                className="at-table__inline-textarea" rows={4}
                placeholder="Relato completo del hecho..." />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginTop: "0.75rem" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                <label style={{ fontSize: "0.82rem", fontWeight: 600, color: "#555", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                  {t("prior_conditions")}
                </label>
                <textarea value={rc.priorConditions}
                  onChange={(e) => setRootCause("priorConditions", e.target.value)}
                  className="at-table__inline-textarea" rows={3}
                  style={{ resize: "vertical" }}
                  placeholder="Qué pasaba antes del evento: volumen de trabajo, ambiente..." />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                <label style={{ fontSize: "0.82rem", fontWeight: 600, color: "#555", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                  {t("permits_ppe_controls")}
                </label>
                <textarea value={rc.permitsEPP}
                  onChange={(e) => setRootCause("permitsEPP", e.target.value)}
                  className="at-table__inline-textarea" rows={3}
                  style={{ resize: "vertical" }}
                  placeholder="Qué controles existían y cuáles fallaron..." />
              </div>
            </div>
            </CollapseSection>

            {/* ── MÉTODO 1: 5 PORQUÉS ── */}
            {isFiveWhys && (
              <CollapseSection title={t("method_5_whys")} mt>
                <div className="at-table__field-row">
                  <label>{t("event_starting_point")}</label>
                  <input type="text" value={rc.event}
                    onChange={(e) => setRootCause("event", e.target.value)}
                    className="at-table__inline-input"
                    placeholder="Resumir el evento en 1 línea" />
                </div>
                {[1,2,3,4,5].map((n) => (
                  <div key={n} className="at-table__field-row">
                    <label>¿Por qué {n}?{n === 5 ? " (raíz)" : ""}</label>
                    <input type="text"
                      value={rc.whys[n-1] || ""}
                      onChange={(e) => {
                        const whys = [...rc.whys];
                        whys[n-1] = e.target.value;
                        setRootCause("whys", whys);
                      }}
                      className="at-table__inline-input"
                      placeholder={
                        n === 1 ? "Causa inmediata — qué falló físicamente en el momento"
                        : n === 2 ? "Causa intermedia — condición que permitió la inmediata"
                        : n === 5 ? "Causa raíz — decisión de gestión que origina toda la cadena"
                        : ""
                      } />
                  </div>
                ))}
              </CollapseSection>
            )}

            {/* ── MÉTODO 2: ISHIKAWA ── */}
            {isFishbone && (
              <CollapseSection title={t("method_ishikawa")} mt>
                <div style={{ width: "100%", marginBottom: "1rem", padding: "0.5rem 0.75rem", background: "#FAFAFA", borderRadius: 6, border: "1px solid #E5E7EB" }}>
                  <svg viewBox="0 0 580 180" style={{ width: "100%", maxHeight: "180px" }} xmlns="http://www.w3.org/2000/svg">
                    <line x1="55" y1="90" x2="510" y2="90" stroke="#9CA3AF" strokeWidth="2.5"/>
                    <ellipse cx="534" cy="90" rx="25" ry="17" fill="#F3F4F6" stroke="#6B7280" strokeWidth="1.5"/>
                    <text x="534" y="94" textAnchor="middle" fontSize="8" fill="#374151" fontWeight="700">EVENTO</text>
                    <polygon points="55,90 37,73 37,107" fill="#D1D5DB"/>
                    <line x1="105" y1="34" x2="168" y2="90" stroke="#3B82F6" strokeWidth="2"/>
                    <text x="105" y="21" textAnchor="middle" fontSize="9" fill="#3B82F6" fontWeight="700">Mano de obra</text>
                    <line x1="253" y1="34" x2="298" y2="90" stroke="#F59E0B" strokeWidth="2"/>
                    <text x="253" y="21" textAnchor="middle" fontSize="9" fill="#F59E0B" fontWeight="700">Material</text>
                    <line x1="393" y1="34" x2="423" y2="90" stroke="#8B5CF6" strokeWidth="2"/>
                    <text x="393" y="21" textAnchor="middle" fontSize="9" fill="#8B5CF6" fontWeight="700">Medio ambiente</text>
                    <line x1="105" y1="146" x2="168" y2="90" stroke="#EF4444" strokeWidth="2"/>
                    <text x="105" y="163" textAnchor="middle" fontSize="9" fill="#EF4444" fontWeight="700">Máquina / Equipo</text>
                    <line x1="253" y1="146" x2="298" y2="90" stroke="#10B981" strokeWidth="2"/>
                    <text x="253" y="163" textAnchor="middle" fontSize="9" fill="#10B981" fontWeight="700">Método</text>
                    <line x1="393" y1="146" x2="423" y2="90" stroke="#EC4899" strokeWidth="2"/>
                    <text x="393" y="163" textAnchor="middle" fontSize="9" fill="#EC4899" fontWeight="700">Medición</text>
                  </svg>
                </div>
                <div className="at-table__field-grid">
                  <div className="at-table__field-row">
                    <label style={{ color: "#3B82F6", fontWeight: 600 }}>{t("manpower_causes")}</label>
                    <textarea value={rc.ishikawaManpower} onChange={(e) => setRootCause("ishikawaManpower", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="Capacitación, fatiga, comportamiento..." />
                  </div>
                  <div className="at-table__field-row">
                    <label style={{ color: "#EF4444", fontWeight: 600 }}>{t("machine_causes")}</label>
                    <textarea value={rc.ishikawaMachine} onChange={(e) => setRootCause("ishikawaMachine", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="Fallas, mantenimiento, antigüedad..." />
                  </div>
                  <div className="at-table__field-row">
                    <label style={{ color: "#F59E0B", fontWeight: 600 }}>{t("material_causes")}</label>
                    <textarea value={rc.ishikawaMaterial} onChange={(e) => setRootCause("ishikawaMaterial", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="Calidad, especificaciones, insumos..." />
                  </div>
                  <div className="at-table__field-row">
                    <label style={{ color: "#10B981", fontWeight: 600 }}>{t("method_causes")}</label>
                    <textarea value={rc.ishikawaMethod} onChange={(e) => setRootCause("ishikawaMethod", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="Procedimientos, instrucciones, ergonomía..." />
                  </div>
                  <div className="at-table__field-row">
                    <label style={{ color: "#8B5CF6", fontWeight: 600 }}>{t("environment_causes")}</label>
                    <textarea value={rc.ishikawaEnvironment} onChange={(e) => setRootCause("ishikawaEnvironment", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="Iluminación, ruido, temperatura, orden..." />
                  </div>
                  <div className="at-table__field-row">
                    <label style={{ color: "#EC4899", fontWeight: 600 }}>{t("measurement_causes")}</label>
                    <textarea value={rc.ishikawaMeasurement} onChange={(e) => setRootCause("ishikawaMeasurement", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="Controles, inspecciones, monitoreo..." />
                  </div>
                </div>
              </CollapseSection>
            )}

            {/* ── MÉTODO 3: ÁRBOL DE CAUSAS ── */}
            {isCauseTree && (
              <CollapseSection title={t("method_cause_tree")} mt>
                <div className="at-table__field-grid">
                  <div className="at-table__field-row">
                    <label style={{ color: "#EF4444", fontWeight: 600 }}>{t("level_0")}</label>
                    <textarea value={rc.causeTreeLevel0} onChange={(e) => setRootCause("causeTreeLevel0", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="Descripción del evento — top del árbol" />
                  </div>
                  <div className="at-table__field-row">
                    <label style={{ color: "#F97316", fontWeight: 600 }}>{t("level_1")}</label>
                    <textarea value={rc.causeTreeLevel1} onChange={(e) => setRootCause("causeTreeLevel1", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="Causas directas. Separar con saltos de línea..." />
                  </div>
                  <div className="at-table__field-row">
                    <label style={{ color: "#EAB308", fontWeight: 600 }}>{t("level_2")}</label>
                    <textarea value={rc.causeTreeLevel2} onChange={(e) => setRootCause("causeTreeLevel2", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="Condiciones que permitieron las inmediatas..." />
                  </div>
                  <div className="at-table__field-row">
                    <label style={{ color: "#3B82F6", fontWeight: 600 }}>{t("level_3")}</label>
                    <textarea value={rc.causeTreeLevel3} onChange={(e) => setRootCause("causeTreeLevel3", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="Decisiones de gestión que crearon esas condiciones..." />
                  </div>
                </div>
              </CollapseSection>
            )}

            {/* ── MÉTODO 4: BOW-TIE ── */}
            {isBowtie && (
              <CollapseSection title={t("method_bowtie")} mt>
                <div className="at-table__field-grid">
                  <div className="at-table__field-row">
                    <label style={{ color: "#F97316", fontWeight: 600 }}>{t("threats_causes")}</label>
                    <textarea value={rc.bowtieThreats} onChange={(e) => setRootCause("bowtieThreats", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="¿Qué pudo causar el evento?" />
                  </div>
                  <div className="at-table__field-row">
                    <label>{t("preventive_barriers")}</label>
                    <textarea value={rc.bowtiePreventiveBarriers} onChange={(e) => setRootCause("bowtiePreventiveBarriers", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="¿Qué se hizo o existía para evitarlo?" />
                  </div>
                  <div className="at-table__field-row">
                    <label>{t("failed_barriers")}</label>
                    <textarea value={rc.bowtieFailedBarriers} onChange={(e) => setRootCause("bowtieFailedBarriers", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="¿Qué debió existir y no funcionó?" />
                  </div>
                </div>
                <div className="at-table__field-row" style={{ background: "#FFF1F2", border: "1.5px solid #FCA5A5", borderRadius: 6, padding: "0.5rem 0.75rem" }}>
                  <label style={{ color: "#DC2626", fontWeight: 700 }}>{t("main_event")} *</label>
                  <textarea value={rc.bowtieMainEvent} onChange={(e) => setRootCause("bowtieMainEvent", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="Descripción del evento central" />
                </div>
                <div className="at-table__field-row" style={{ marginTop: "0.5rem" }}>
                  <label>{t("potential_severity")}</label>
                  <InlineSelect options={BOWTIE_SEVERITY_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={rc.bowtieSeverity}
                    onChange={(v) => setRootCause("bowtieSeverity", v)} />
                </div>
                <div className="at-table__field-grid">
                  <div className="at-table__field-row">
                    <label style={{ color: "#7C3AED", fontWeight: 600 }}>{t("real_consequences")}</label>
                    <textarea value={rc.bowtieConsequences} onChange={(e) => setRootCause("bowtieConsequences", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="¿Qué pasó después del evento?" />
                  </div>
                  <div className="at-table__field-row">
                    <label>{t("mitigating_barriers")}</label>
                    <textarea value={rc.bowtieMitigatingBarriers} onChange={(e) => setRootCause("bowtieMitigatingBarriers", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="¿Qué ayudó a contener el daño?" />
                  </div>
                  <div className="at-table__field-row">
                    <label>{t("missing_mitigations")}</label>
                    <textarea value={rc.bowtieMissingMitigations} onChange={(e) => setRootCause("bowtieMissingMitigations", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="¿Qué debió existir para minimizar el daño?" />
                  </div>
                </div>
              </CollapseSection>
            )}

            {/* ── CAUSAS SEGÚN NIVEL ── */}
            <CollapseSection title={t("causes_by_level")} mt>
            <div className="at-table__field-grid">
              <div className="at-table__field-row">
                <label>{t("immediate_causes")}</label>
                <textarea value={rc.immediateCauses} onChange={(e) => setRootCause("immediateCauses", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="Lo que pasó justo antes del evento..." />
              </div>
              <div className="at-table__field-row">
                <label>{t("underlying_causes")}</label>
                <textarea value={rc.underlyingCauses} onChange={(e) => setRootCause("underlyingCauses", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="Condiciones organizacionales que permitieron las inmediatas..." />
              </div>
              <div className="at-table__field-row">
                <label>{t("latent_causes")}</label>
                <textarea value={rc.latentCauses} onChange={(e) => setRootCause("latentCauses", e.target.value)} className="at-table__inline-textarea" rows={2} placeholder="Decisiones de gestión que crearon esas condiciones..." />
              </div>
            </div>
            </CollapseSection>

            {/* ── CAUSA RAÍZ · CHECKLIST 25 ítems ── */}
            <CollapseSection title={t("root_cause_checklist")} mt>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "2px 1rem", padding: "0.5rem 0.75rem", background: "#FAFAFA", border: "1px solid #E5E7EB", borderRadius: 6 }}>
              {ROOT_CAUSES.map((c) => (
                <label key={c.key} style={{ display: "flex", alignItems: "flex-start", gap: "0.4rem", padding: "4px 2px", fontSize: "0.81rem", cursor: "pointer", borderRadius: 3, lineHeight: "1.3" }}>
                  <input type="checkbox" style={{ marginTop: "2px", flexShrink: 0, accentColor: "#7C3AED" }}
                    checked={rc.causes.includes(c.key)}
                    onChange={() => {
                      const cur = rc.causes;
                      setRootCause("causes", cur.includes(c.key) ? cur.filter((x) => x !== c.key) : [...cur, c.key]);
                    }} />
                  {c.text}
                </label>
              ))}
            </div>
            {rc.causes.includes("other") && (
              <div className="at-table__field-row" style={{ marginTop: "0.5rem" }}>
                <label>{t("other_describe")}</label>
                <input type="text" value={rc.causesOther}
                  onChange={(e) => setRootCause("causesOther", e.target.value)}
                  className="at-table__inline-input" />
              </div>
            )}
            </CollapseSection>

            {/* ── FACTORES CONTRIBUYENTES · 5 ítems ── */}
            <CollapseSection title={t("contributing_factors_section")} mt>
            <div style={{ display: "flex", gap: "0.5rem 2rem", flexWrap: "wrap", padding: "0.6rem 0.75rem", background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 6 }}>
              {CONTRIBUTING_FACTORS.map((f) => (
                <label key={f} style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.85rem", cursor: "pointer" }}>
                  <input type="checkbox" style={{ accentColor: "#0EA5E9" }}
                    checked={rc.contributingFactors.includes(f)}
                    onChange={() => {
                      const cur = rc.contributingFactors;
                      setRootCause("contributingFactors", cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]);
                    }} />
                  {f}
                </label>
              ))}
            </div>
            </CollapseSection>

            {/* ── LECCIÓN APRENDIDA Y TESTIGOS ── */}
            <CollapseSection title={t("lesson_learned_witnesses")} mt>
            <div className="at-table__field-row">
              <label>{t("lesson_learned")}</label>
              <textarea value={rc.lessonLearned} onChange={(e) => setRootCause("lessonLearned", e.target.value)} className="at-table__inline-textarea" rows={3} placeholder="Resumen para campaña de difusión interna (2-3 líneas)..." />
            </div>
            <WitnessesPanel witnesses={editingData.witnesses} onChange={(v) => setField("witnesses", v)} t={t} />
            </CollapseSection>

            {/* ── Estado del registro ── */}
            <div className="at-table__field-grid" style={{ marginTop: "0.75rem" }}>
              <div className="at-table__field-row">
                <label>{t("record_status")}</label>
                <InlineSelect options={RECORD_STATUS_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.recordStatus}
                  onChange={(v) => setField("recordStatus", v)} placeholder={t("select")} />
              </div>
            </div>
          </div>
        );
      })(),
      capa: (() => {
        const handleGlobalResult = (v) => {
          setField("capaGlobalResult", v);
          if (v === "reopen") setField("caseStatus", "reopened");
        };
        return (
          <div className="at-table__section-fields">
            <CapaPanel actions={editingData.correctiveActions} onChange={(v) => setField("correctiveActions", v)} t={t} />

            {/* ── VERIFICACIÓN DE EFICACIA POST-CIERRE ── */}
            <div style={{ marginTop: "1.5rem", padding: "1rem 1.1rem", background: "#F7FAFF", border: "1.5px solid #C5D5EE", borderRadius: 8 }}>
              <CollapseSection title={t("post_closure_verification")}>
              <div className="at-table__field-grid">
                <div className="at-table__field-row">
                  <label>{t("verification_date")}</label>
                  <input type="date" value={editingData.capaVerificationDate} onChange={(e) => setField("capaVerificationDate", e.target.value)} className="at-table__inline-input" />
                </div>
                <div className="at-table__field-row">
                  <label>{t("verifier")}</label>
                  <input type="text" value={editingData.capaVerifier} onChange={(e) => setField("capaVerifier", e.target.value)} className="at-table__inline-input" placeholder="Quien verifica el plan completo" />
                </div>
                <div className="at-table__field-row">
                  <label>{t("global_result")}</label>
                  <InlineSelect options={GLOBAL_RESULT_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.capaGlobalResult} onChange={handleGlobalResult} />
                </div>
                <div className="at-table__field-row">
                  <label>{t("next_review")}</label>
                  <input type="date" value={editingData.capaNextReview} onChange={(e) => setField("capaNextReview", e.target.value)} className="at-table__inline-input" />
                </div>
              </div>
              {editingData.capaGlobalResult === "reopen" && (
                <div style={{ marginTop: "0.6rem", padding: "7px 12px", background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 6, fontSize: "0.8rem", color: "#DC2626", fontWeight: 600 }}>
                  ⚠ El estado del caso cambiará a Reabierto automáticamente al guardar.
                </div>
              )}
              </CollapseSection>
            </div>
          </div>
        );
      })(),
      closure: (() => {
        const onFileUploaded = (f) =>
          setEditingData((p) => ({ ...p, files: [...(p.files || []), f] }));
        const onFileDeleted = (key) =>
          setEditingData((p) => ({ ...p, files: (p.files || []).filter((f) => f.key !== key) }));

        return (
          <div className="at-table__section-fields">
            {/* ── NOTIFICACIONES ── */}
            <CollapseSection title={t("notifications")}>
            <div className="at-table__field-grid">
              <div className="at-table__field-row">
                <label>{t("notified_art")}</label>
                <InlineSelect options={ART_NOTIFIED_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.artNotified} onChange={(v) => setField("artNotified", v)} />
              </div>
              {editingData.artNotified === "yes" && (
                <div className="at-table__field-row">
                  <label>{t("art_notification_date")}</label>
                  <input type="date" value={editingData.artNotificationDate} onChange={(e) => setField("artNotificationDate", e.target.value)} className="at-table__inline-input" />
                </div>
              )}
              <div className="at-table__field-row">
                <label>{t("claim_number")}</label>
                <input type="text" value={editingData.claimNumber} onChange={(e) => setField("claimNumber", e.target.value)} className="at-table__inline-input" placeholder="Identificador ante la aseguradora" />
              </div>
              <div className="at-table__field-row">
                <label>{t("notified_labor_authority")}</label>
                <InlineSelect options={LABOR_AUTH_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.laborAuthorityNotified} onChange={(v) => setField("laborAuthorityNotified", v)} />
              </div>
              <div className="at-table__field-row">
                <label>{t("notified_client")}</label>
                <InlineSelect options={CLIENT_NOTIFIED_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.clientNotified} onChange={(v) => setField("clientNotified", v)} />
              </div>
              {(editingData.clientNotified === "yes" || editingData.clientNotified === "pending") && (
                <div className="at-table__field-row">
                  <label>{t("client_and_date")}</label>
                  <input type="text" value={editingData.clientNotificationDetails} onChange={(e) => setField("clientNotificationDetails", e.target.value)} className="at-table__inline-input" placeholder="Ej: PUMA · 23/04/2026" />
                </div>
              )}
            </div>

            </CollapseSection>

            {/* ── COMUNICACIÓN INTERNA ── */}
            <CollapseSection title={t("internal_communication")} mt>
            <div className="at-table__field-grid">
              <div className="at-table__field-row">
                <label>{t("communication_done")}</label>
                <InlineSelect options={INTERNAL_COMM_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.internalCommunicationDone} onChange={(v) => setField("internalCommunicationDone", v)} />
              </div>
              <div className="at-table__field-row">
                <label>{t("communication_date")}</label>
                <input type="date" value={editingData.internalCommunicationDate} onChange={(e) => setField("internalCommunicationDate", e.target.value)} className="at-table__inline-input" />
              </div>
              <div className="at-table__field-row">
                <label>{t("sst_committee_informed")}</label>
                <InlineSelect options={SST_COMMITTEE_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.sstCommitteeInformed} onChange={(v) => setField("sstCommitteeInformed", v)} />
              </div>
              <div className="at-table__field-row">
                <label>{t("toolbox_talk")}</label>
                <InlineSelect options={SST_COMMITTEE_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={editingData.toolboxTalkDone} onChange={(v) => setField("toolboxTalkDone", v)} />
              </div>
            </div>

            </CollapseSection>

            {/* ── ADJUNTOS ── */}
            <CollapseSection title={t("attachments")} mt>
            {(!editingId || editingId === "new") && (
              <div style={{ padding: "8px 12px", background: "#F3F4F6", borderRadius: 6, fontSize: "0.8rem", color: "#6B7280", marginBottom: "0.75rem" }}>
                Guarda el registro primero para poder adjuntar archivos.
              </div>
            )}
            <div className="at-table__closure-uploads">
              <InlineCategoryUpload category="fotos"             label={t("photos")}             accept="image/*"              multiple files={editingData.files} editingId={editingId} accessToken={accessToken} onFileUploaded={onFileUploaded} onFileDeleted={onFileDeleted} />
              <InlineCategoryUpload category="certificado_medico" label={t("medical_certificate")} accept=".pdf,.jpg,.jpeg,.png" multiple={false} files={editingData.files} editingId={editingId} accessToken={accessToken} onFileUploaded={onFileUploaded} onFileDeleted={onFileDeleted} />
              <InlineCategoryUpload category="parte_art"          label={t("art_report")}          accept=".pdf,.jpg,.jpeg,.png" multiple={false} files={editingData.files} editingId={editingId} accessToken={accessToken} onFileUploaded={onFileUploaded} onFileDeleted={onFileDeleted} />
              <InlineCategoryUpload category="otros"              label={t("other_documents")}     accept="*"                    multiple files={editingData.files} editingId={editingId} accessToken={accessToken} onFileUploaded={onFileUploaded} onFileDeleted={onFileDeleted} />
            </div>
            </CollapseSection>
          </div>
        );
      })(),

      plan_accion: (() => {
        const STATUS_LABEL = {
          pending:             { label: "Pendiente",        bg: "#FEF3C7", color: "#92400E" },
          in_progress:         { label: "En progreso",      bg: "#DBEAFE", color: "#1E40AF" },
          implemented_pending: { label: "Verif. pendiente", bg: "#FEF9C3", color: "#713F12" },
          closed:              { label: "Cerrada",          bg: "#DCFCE7", color: "#166534" },
          overdue:             { label: "Atrasada",         bg: "#FEE2E2", color: "#991B1B" },
          cancelled:           { label: "Cancelada",        bg: "#F1F5F9", color: "#64748B" },
        };
        const PRIORITY_LABEL = {
          high:   { label: "Alta",  color: "#DC2626" },
          medium: { label: "Media", color: "#D97706" },
          low:    { label: "Baja",  color: "#16A34A" },
        };
        const fmt = (d) => d ? new Date(d).toLocaleDateString("es-AR") : "—";

        if (!linkedPlan) {
          return (
            <div className="at-table__section-fields">
              <div style={{ background: "#F8FAFC", border: "1px dashed #CBD5E1", borderRadius: 10, padding: "1.5rem 1.2rem" }}>
                <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#1E293B", marginBottom: "0.35rem" }}>
                  {t("create_action_plan")}
                </div>
                <div style={{ fontSize: "0.8rem", color: "#64748B", marginBottom: "1rem" }}>
                  Se vinculará automáticamente a este accidente.
                </div>
                <input
                  type="text"
                  value={planTitle}
                  onChange={e => setPlanTitle(e.target.value)}
                  placeholder={`Accidente: ${editingData.employee || "Sin empleado"} — ${editingData.date}`}
                  className="at-table__inline-input"
                  style={{ width: "100%", marginBottom: "0.75rem" }}
                  disabled={creatingPlan}
                />
                <button
                  type="button"
                  onClick={handleCreatePlan}
                  disabled={creatingPlan}
                  style={{ padding: "0.45rem 1.2rem", background: "#4A6F9A", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, fontSize: "0.85rem", cursor: creatingPlan ? "not-allowed" : "pointer", opacity: creatingPlan ? 0.7 : 1 }}
                >
                  {creatingPlan ? t("creating") : t("create_action_plan")}
                </button>
              </div>
            </div>
          );
        }

        const st = STATUS_LABEL[linkedPlan.status] || STATUS_LABEL.pending;
        const pr = PRIORITY_LABEL[linkedPlan.priority] || PRIORITY_LABEL.medium;
        const isOverdue = linkedPlan.dueDate && new Date(linkedPlan.dueDate) < new Date()
          && linkedPlan.status !== "closed" && linkedPlan.status !== "cancelled";

        return (
          <div className="at-table__section-fields">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
              <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#1E293B" }}>{t("linked_action_plan")}</div>
              <span style={{ fontSize: "0.76rem", color: "#64748B" }}>{t("manage_from_action_plan")}</span>
            </div>

            <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, padding: "1rem 1.1rem", background: "#fff", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                <div style={{ fontWeight: 700, fontSize: "0.92rem", color: "#1E293B", lineHeight: 1.3 }}>{linkedPlan.title}</div>
                <span style={{ background: st.bg, color: st.color, borderRadius: 10, padding: "3px 12px", fontSize: "0.75rem", fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 }}>
                  {st.label}
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "0.5rem 1.5rem", fontSize: "0.82rem", color: "#475569" }}>
                <div>{t("priority")}: <strong style={{ color: pr.color }}>{pr.label}</strong></div>
                {linkedPlan.responsible && <div>{t("responsible")}: <strong>{linkedPlan.responsible}</strong></div>}
                {linkedPlan.dueDate && (
                  <div style={{ color: isOverdue ? "#DC2626" : undefined }}>
                    {t("due_date")}: <strong>{fmt(linkedPlan.dueDate)}</strong>{isOverdue && " ⚠️"}
                  </div>
                )}
                {linkedPlan.progress > 0 && <div>{t("progress")}: <strong>{linkedPlan.progress}%</strong></div>}
              </div>

              {linkedPlan.progress > 0 && (
                <div style={{ background: "#E2E8F0", borderRadius: 4, height: 6, marginTop: 2 }}>
                  <div style={{ width: `${linkedPlan.progress}%`, background: linkedPlan.progress === 100 ? "#16A34A" : "#3B82F6", borderRadius: 4, height: "100%", transition: "width 0.3s" }} />
                </div>
              )}

              {linkedPlan.findingDescription && (
                <div style={{ fontSize: "0.79rem", color: "#64748B", paddingTop: 4, borderTop: "1px solid #F1F5F9" }}>
                  {linkedPlan.findingDescription}
                </div>
              )}
            </div>
          </div>
        );
      })(),
    };

    return (
      <div className="at-table__section-panel">
        <div className="at-table__section-nav">
          {SECTIONS.map((s) => (
            <button key={s.key} type="button"
              className={`at-table__section-btn${activeSection === s.key ? " active" : ""}`}
              onClick={() => setActiveSection(s.key)}
            >{s.label}</button>
          ))}
        </div>
        {activeSection && (
          <div className="at-table__section-body">
            {panelContent[activeSection]}
          </div>
        )}
      </div>
    );
  };

  if (items === null) return <LoadingSpinner />;

  return (
    <div className="at-table">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Export row */}
      <div className="at-table__export-row">
        {/* Export dropdown */}
        <div className="at-table__export-dropdown" ref={exportMenuRef}>
          <button className="at-table__export-toggle" onClick={() => setShowExportMenu((v) => !v)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {t("export_btn")}
            <svg viewBox="0 0 12 12" fill="currentColor" width="9" height="9"><path d="M6 8L2 4h8z"/></svg>
          </button>
          {showExportMenu && (
            <div className="at-table__export-menu">
              <button className="at-table__export-menu-item" onClick={() => { handleExportCSV(); setShowExportMenu(false); }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                CSV
              </button>
              <button className="at-table__export-menu-item" onClick={() => { handleExportXLSX(); setShowExportMenu(false); }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                Excel (XLSX)
              </button>
              <button className="at-table__export-menu-item" onClick={() => { handleExportPDF(); setShowExportMenu(false); }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                PDF
              </button>
            </div>
          )}
        </div>
        {/* New button — solo si hay sitio seleccionado */}
        {!noSiteInCtx && (
          <button className="at-table__new-btn" onClick={startNew} title={t("new_accident")}>
            <svg viewBox="0 0 16 16" fill="currentColor" width="15" height="15">
              <path d="M8 2a1 1 0 0 1 1 1v4h4a1 1 0 1 1 0 2H9v4a1 1 0 1 1-2 0V9H3a1 1 0 1 1 0-2h4V3a1 1 0 0 1 1-1z"/>
            </svg>
          </button>
        )}
      </div>

      {/* Filtros + tabla, en una sola card */}
      <div className="at-table__card">
        <div className="at-table__filters">
          <div className="at-table__filter-block at-table__filter-search">
            <div className="at-table__search-wrap">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="at-table__filter-input"
                placeholder="Buscar"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          {(!contextSite || contextSite === "0" || contextSite === 0) && (
            <div className="at-table__filter-block">
              <FilterDropdown
                options={filterSites.map((s) => ({ key: s._id, value: s._id, text: s.name }))}
                value={siteFilter}
                onChange={setSiteFilter}
                placeholder={t("site")}
              />
            </div>
          )}
          <div className="at-table__filter-block">
            <FilterDropdown
              options={accidentMechOptions}
              value={typeFilter}
              onChange={setTypeFilter}
              placeholder={t("acc_form_type")}
            />
          </div>
          <div className="at-table__filter-block">
            <FilterDropdown
              options={CASE_TYPE_OPTIONS.map((o) => ({ ...o, text: t(o.value) }))}
              value={classFilter}
              onChange={setClassFilter}
              placeholder={t("acc_case_type")}
            />
          </div>
          <div className="at-table__filter-block">
            <FilterDropdown
              options={CASE_STATUS_OPTIONS.map((o) => ({ ...o, text: t(o.value) }))}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder={t("status")}
            />
          </div>
        </div>

        {/* Quick creation bar - visible above the table when creating new record */}
        {editingId === "new" && renderQuickBar()}

        {/* Table */}
        {itemsFilter.length === 0 ? (
          <div className="at-table__empty">{t("no_results")}</div>
        ) : (
        <div className="at-table__tbl-scroll">
        <Table className="at-table__tbl unstackable rtable-cards" selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>#</Table.HeaderCell>
              <Table.HeaderCell>{t("date")}</Table.HeaderCell>
              <Table.HeaderCell>{t("site")}</Table.HeaderCell>
              <Table.HeaderCell>{t("division")}</Table.HeaderCell>
              <Table.HeaderCell>{t("type")}</Table.HeaderCell>
              <Table.HeaderCell>{t("acc_col_form")}</Table.HeaderCell>
              <Table.HeaderCell>{t("acc_col_part")}</Table.HeaderCell>
              <Table.HeaderCell>{t("employee")}</Table.HeaderCell>
              <Table.HeaderCell>{t("acc_col_days")}</Table.HeaderCell>
              <Table.HeaderCell>{t("status")}</Table.HeaderCell>
              <Table.HeaderCell textAlign="center"></Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {itemsFilter.map((a) => (
                <Table.Row key={a._id}>
                  {/* 1. ID */}
                  <Table.Cell data-label="#">
                    <span className="at-table__id-badge">{formatAccidentId(a)}</span>
                  </Table.Cell>
                  {/* 2. Fecha */}
                  <Table.Cell data-label={t("date")} style={{ whiteSpace: "nowrap" }}>{formatDate(a.date)}</Table.Cell>
                  {/* 3. Sitio */}
                  <Table.Cell data-label={t("site")}>{a.site?.name || "-"}</Table.Cell>
                  {/* 4. División */}
                  <Table.Cell data-label={t("division")}>{a.division?.name || "-"}</Table.Cell>
                  {/* 5. Tipo */}
                  <Table.Cell data-label={t("type")}>
                    {t(a.caseType) || "-"}
                  </Table.Cell>
                  {/* 6. Forma */}
                  <Table.Cell data-label={t("acc_col_form")}>{a.accidentMechanism?.name || accidentMechOptions.find(m => m.key === (a.accidentMechanism?._id || a.accidentMechanism))?.text || "-"}</Table.Cell>
                  {/* 7. Parte */}
                  <Table.Cell data-label={t("acc_col_part")}>
                    {!a.bodyParts?.length
                      ? "-"
                      : a.bodyParts.length === 1
                        ? a.bodyParts[0]
                        : `${a.bodyParts[0]} +Múltiples`}
                  </Table.Cell>
                  {/* 8. Empleado */}
                  <Table.Cell data-label={t("employee")}>{a.employee || "-"}</Table.Cell>
                  {/* 9. Días */}
                  <Table.Cell data-label={t("acc_col_days")}>{a.lostDays ?? "-"}</Table.Cell>
                  {/* 10. Estado */}
                  <Table.Cell data-label={t("status")}>
                    {(() => {
                      const cfg = STATUS_CONFIG[a.caseStatus] || STATUS_CONFIG.draft;
                      return (
                        <span style={{
                          display: "inline-block",
                          padding: "2px 10px",
                          borderRadius: "999px",
                          fontSize: "0.78em",
                          fontWeight: 600,
                          color: cfg.color,
                          backgroundColor: cfg.bg,
                          whiteSpace: "nowrap",
                        }}>
                          {t(a.caseStatus)}
                        </span>
                      );
                    })()}
                  </Table.Cell>
                  {/* 11. Acciones */}
                  <Table.Cell textAlign="center" className="at-table__actions" onClick={(e) => e.stopPropagation()}>
                    <div className="at-table__row-menu">
                      <button
                        type="button"
                        className="at-table__row-menu-btn"
                        onClick={(e) => toggleRowMenu(a._id, e)}
                      >
                        <Icon name="ellipsis vertical" />
                      </button>
                    </div>
                    {openRowMenu?.id === a._id && ReactDOM.createPortal(
                      <div
                        className="at-table__row-menu-list"
                        style={{
                          position: "fixed",
                          top: openRowMenu.top,
                          left: openRowMenu.left,
                          transform: openRowMenu.openUp ? "translateY(-100%)" : "none",
                        }}
                      >
                        <button onClick={() => { startEdit(a); setOpenRowMenu(null); }} disabled={editingId !== null}>
                          <Icon name="pencil" />{t("edit")}
                        </button>
                        <button className="at-table__row-menu-item--danger" onClick={() => { openDelete(a); setOpenRowMenu(null); }} disabled={editingId !== null}>
                          <Icon name="trash" />{t("delete")}
                        </button>
                      </div>,
                      document.body
                    )}
                  </Table.Cell>
                </Table.Row>
            ))}
          </Table.Body>
        </Table>
        </div>
        )}
      </div>

      {/* Full accident form modal */}
      {renderFormModal()}

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? t("delete") : (selectedItem?.active ? t("deactivate") : t("activate"))}
        message={
          isDelete
            ? `${t("confirm_delete_accident")} ${formatAccidentId(selectedItem || {})}?`
            : `${t(selectedItem?.active ? "deactivate" : "activate")} "${selectedItem?.employee}"?`
        }
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
        confirmLabel={t("accept")}
        cancelLabel={t("cancel")}
        confirmDanger={isDelete}
      />

      {/* Portal: agregar nuevo catálogo (tipo accidente, medio ambiente, forma, desviación, agente, proceso, naturaleza) */}
      <AddCatalogPortal
        open={addCatalog !== null}
        title={{
          accidentType:  "Nuevo tipo de accidente",
          workEnv:       "Nuevo medio ambiente de trabajo",
          mechanism:     "Nueva forma de accidente",
          deviation:     "Nueva desviación",
          materialAgent: "Nuevo agente material",
          workProcess:   "Nuevo proceso de trabajo",
          injuryNature:  "Nueva naturaleza de lesión",
          ciiu:          "Nueva actividad CIIU",
          division:      "Nueva división",
          sector:        "Nuevo sector",
          ocupacion:     "Nueva ocupación",
        }[addCatalog] || ""}
        hasCode={addCatalog === "accidentType" || addCatalog === "ciiu"}
        onClose={() => setAddCatalog(null)}
        onSave={async (values) => {
          switch (addCatalog) {
            case "accidentType":  await accidentTypeController.createAccidentType(accessToken, values);              reloadAccidentTypes(true);      break;
            case "workEnv":       await workEnvController.createWorkEnvironment(accessToken, { name: values.name }); reloadWorkEnvs(true);           break;
            case "mechanism":     await accidentMechanismCtrl.createAccidentMechanism(accessToken, { name: values.name }); reloadMechs(true);        break;
            case "deviation":     await deviationCtrl.createDeviation(accessToken, { name: values.name });           reloadDeviations(true);         break;
            case "materialAgent": await materialAgentCtrl.createMaterialAgent(accessToken, { name: values.name });   reloadMatAgents(null, true);    break;
            case "workProcess":   await workProcessCtrl.createWorkProcess({ name: values.name });                    reloadWorkProcesses(true);      break;
            case "injuryNature":  await injuryNatureCtrl.createInjuryNature({ name: values.name });                  reloadInjuryNatures(true);      break;
            case "ciiu":          await ciiuActivityCtrl.createCiiuActivity(accessToken, values);                    reloadCiiuOptions(true);        break;
            case "division":      await divisionController.createDivision(accessToken, { name: values.name });       reloadDivisions(true);          break;
            case "sector":        await sectorController.createSector(accessToken, { name: values.name });           reloadSectors(true);            break;
            case "ocupacion":     await ocupacionController.createOcupacion(accessToken, { name: values.name });     reloadOcupaciones(true);        break;
            default: break;
          }
        }}
      />

      {/* Modal adjuntar archivos */}
      <BasicModal show={showAttach} close={() => setShowAttach(false)} title={t("attach_files")} size="small">
        <div>
          <p style={{ color: "#666", marginBottom: "0.5rem" }}>{t("select_files_to_attach")}</p>
          <input type="file" multiple ref={fileInputRef} style={{ marginBottom: "1rem", display: "block" }} />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button primary loading={uploading} disabled={uploading} onClick={handleUpload}>{t("upload")}</Button>
            <Button onClick={() => setShowAttach(false)} disabled={uploading}>{t("cancel")}</Button>
          </div>
        </div>
      </BasicModal>

      {/* Modal ver archivos */}
      <BasicModal show={showFiles} close={() => setShowFiles(false)} title={t("attached_files")} size="small">
        <div>
          {!fileItem?.files?.length ? (
            <p style={{ color: "#999", textAlign: "center" }}>{t("no_attached_files")}</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {fileItem.files.map((f, i) => (
                <li key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem" }}>
                  <Icon name={/\.(jpg|jpeg|png|gif|webp)$/i.test(f.name) ? "image outline" : "file outline"} />
                  <span style={{ flex: 1, fontSize: "0.88rem" }}>{f.name}</span>
                  <Button circular icon size="mini" style={{ backgroundColor: "#648EBC", color: "#fff" }} onClick={() => handleViewFile(f.key, f.name)}>
                    <Icon name="eye" />
                  </Button>
                  <EvidenceTextButton module="accident" fileKey={f.key} fileName={f.name} className="ev-text-btn--mini" />

                  <Button circular icon size="mini" style={{ backgroundColor: "#A4C4E0", color: "#fff" }}
                    loading={deletingFile === f.key} disabled={deletingFile !== null}
                    onClick={() => handleDeleteFile(f.key)}>
                    <Icon name="trash" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "0.75rem" }}>
            <Button onClick={() => setShowFiles(false)} style={{ backgroundColor: "#D9D9D9", color: "#555", opacity: 0.8 }}>{t("close")}</Button>
          </div>
        </div>
      </BasicModal>

      {/* Modal observaciones */}
      <BasicModal show={showObs} close={() => { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); }} title={t("observations")} size="small">
        <div className="lr-table__modal-inner">
          {obsItem?.observations?.length > 0 && (
            <div className="lr-table__obs-history">
              {[...obsItem.observations].map((obs, origIdx) => ({ obs, origIdx })).reverse().map(({ obs, origIdx }) => {
                const currentUser = [user?.name, user?.lastname].filter(Boolean).join(" ") || "Usuario";
                const isOwner = obs.userName === currentUser;
                const isEditing = editingObsIdx === origIdx;
                return (
                  <div key={origIdx} className="lr-table__obs-entry">
                    <div className="lr-table__obs-meta">
                      <span className="lr-table__obs-user">{obs.userName}</span>
                      <span className="lr-table__obs-date">
                        {obs.createdAt ? new Date(obs.createdAt).toLocaleDateString() : ""}
                      </span>
                      {isOwner && !isEditing && (
                        <span className="lr-table__obs-actions">
                          <Icon name="pencil" link onClick={() => { setEditingObsIdx(origIdx); setEditingObsVal(obs.text); setEditingObsDate(obs.createdAt ? new Date(obs.createdAt).toISOString().split("T")[0] : ""); }} />
                          <Icon name="trash alternate" link color="red" onClick={() => handleDeleteObs(origIdx)} />
                        </span>
                      )}
                    </div>
                    {isEditing ? (
                      <div>
                        <input
                          type="date"
                          value={editingObsDate}
                          onChange={(e) => setEditingObsDate(e.target.value)}
                          style={{ marginBottom: "0.4rem", padding: "4px 8px", borderRadius: "6px", border: "1px solid rgba(34,36,38,0.2)", fontSize: "0.85rem" }}
                        />
                        <TextArea
                          rows={2}
                          value={editingObsVal}
                          onChange={(e) => setEditingObsVal(e.target.value)}
                          style={{ width: "100%", marginBottom: "0.4rem" }}
                        />
                        <div style={{ display: "flex", gap: "0.4rem" }}>
                          <Button size="mini" primary loading={saving} disabled={saving || !editingObsVal.trim()} onClick={handleSaveObsEdit}>{t("save")}</Button>
                          <Button size="mini" onClick={() => { setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); }}>{t("cancel")}</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="lr-table__obs-text">{obs.text}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <TextArea
            rows={3}
            value={obsVal}
            onChange={(e) => setObsVal(e.target.value)}
            placeholder={t("enter_observations")}
            style={{ width: "100%", marginTop: "0.75rem" }}
          />
          <div className="lr-table__modal-actions">
            <Button primary loading={saving} disabled={saving || !obsVal.trim()} onClick={handleSaveObs}>{t("save")}</Button>
            <Button onClick={() => { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); }}>{t("cancel")}</Button>
          </div>
        </div>
      </BasicModal>
    </div>
  );
}

/* ── Sub-components ──────────────────────────────── */

function WitnessesPanel({ witnesses, onChange, t }) {
  const [newW, setNewW] = useState({ name: "", testimony: "" });
  const add = () => {
    if (!newW.name) return;
    onChange([...witnesses, { ...newW }]);
    setNewW({ name: "", testimony: "" });
  };
  return (
    <div style={{ marginTop: "1rem" }}>
      <h4 style={{ marginBottom: "0.5rem" }}>Testigos</h4>
      <div className="at-table__field-grid">
        <div className="at-table__field-row">
          <label>Nombre del testigo</label>
          <input type="text" value={newW.name} onChange={(e) => setNewW((p) => ({ ...p, name: e.target.value }))}
            className="at-table__inline-input" />
        </div>
        <div className="at-table__field-row">
          <label>Relato</label>
          <textarea value={newW.testimony} onChange={(e) => setNewW((p) => ({ ...p, testimony: e.target.value }))}
            className="at-table__inline-textarea" rows={2} />
        </div>
      </div>
      <Button type="button" size="small" onClick={add} style={{ marginTop: "0.4rem" }}>Agregar testigo</Button>
      {witnesses.length > 0 && (
        <table className="at-table__mini-table">
          <thead><tr><th>Nombre del testigo</th><th>Relato</th><th></th></tr></thead>
          <tbody>
            {witnesses.map((w, i) => (
              <tr key={i}>
                <td>{w.name}</td>
                <td>{w.testimony}</td>
                <td><Button icon size="mini" color="red" type="button" onClick={() => onChange(witnesses.filter((_, idx) => idx !== i))}><Icon name="trash" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function InlineCategoryUpload({ category, label, accept, multiple, files, editingId, accessToken, onFileUploaded, onFileDeleted }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);
  const [viewingKey, setViewingKey] = useState(null);

  const categoryFiles = (files || []).filter((f) => f.category === category);
  const disabled = !editingId || editingId === "new";

  const handleView = async (key) => {
    setViewingKey(key);
    try {
      const blobUrl = await accidentController.getFile(accessToken, key);
      if (!blobUrl) { toast.error("No se pudo abrir el archivo"); return; }
      window.open(blobUrl, "_blank");
    } catch { toast.error("No se pudo abrir el archivo"); }
    setViewingKey(null);
  };

  const handleChange = async (e) => {
    const selected = e.target.files;
    if (!selected?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(selected)) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("category", category);
        const result = await accidentController.uploadFile(accessToken, editingId, formData);
        if (result?.file) onFileUploaded({ ...result.file, category });
      }
      toast.success("Archivo subido correctamente");
    } catch { toast.error("Error al subir el archivo"); }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDelete = async (key) => {
    setDeletingKey(key);
    try {
      await accidentController.deleteFile(accessToken, editingId, key);
      onFileDeleted(key);
    } catch { toast.error("Error al eliminar el archivo"); }
    setDeletingKey(null);
  };

  return (
    <div className="at-table__closure-upload">
      <div className="at-table__closure-upload-header">
        <Icon name="paperclip" style={{ color: "#7C3AED", fontSize: "0.85rem", marginRight: 4 }} />
        <span className="at-table__closure-upload-label">{label}</span>
        {!disabled && (
          <label className={`at-table__closure-upload-btn${uploading ? " loading" : ""}`}>
            {uploading ? "Subiendo..." : "+ Adjuntar"}
            <input ref={inputRef} type="file" accept={accept} multiple={multiple} onChange={handleChange} style={{ display: "none" }} disabled={uploading} />
          </label>
        )}
      </div>
      {categoryFiles.length > 0 && (
        <div className="at-table__closure-upload-files">
          {categoryFiles.map((f, i) => (
            <div key={i} className="at-table__closure-file-item">
              <Icon name="file outline" style={{ fontSize: "0.8rem", color: "#648EBC", flexShrink: 0 }} />
              <button
                type="button"
                onClick={() => handleView(f.key)}
                disabled={viewingKey === f.key}
                className="at-table__closure-file-link"
                style={{ background: "none", border: "none", padding: 0, color: "#648EBC", textDecoration: "underline", cursor: "pointer", font: "inherit", textAlign: "left" }}
              >
                {viewingKey === f.key ? "Abriendo…" : f.name}
              </button>
              <button type="button" onClick={() => handleDelete(f.key)} className="at-table__closure-file-del" disabled={deletingKey === f.key}>
                {deletingKey === f.key ? "…" : "×"}
              </button>
            </div>
          ))}
        </div>
      )}
      {categoryFiles.length === 0 && (
        <div style={{ fontSize: "0.75rem", color: "#9CA3AF", paddingLeft: "1.5rem", marginTop: "2px" }}>Sin archivos</div>
      )}
    </div>
  );
}

const EMPTY_ACTION = {
  type: "corrective", controlHierarchy: "", description: "",
  responsible: "", verifier: "", dueDate: "", status: "pending", efficacy: "pending",
};

const HIERARCHY_COLOR = {
  "1_elimination":    { color: "#059669", bg: "#ECFDF5" },
  "2_substitution":   { color: "#10B981", bg: "#F0FDF4" },
  "3_engineering":    { color: "#3B82F6", bg: "#EFF6FF" },
  "4_administrative": { color: "#D97706", bg: "#FFFBEB" },
  "5_epp":            { color: "#DC2626", bg: "#FEF2F2" },
};
const STATUS_STYLE = {
  pending:     { color: "#D97706", bg: "#FFFBEB" },
  in_progress: { color: "#2563EB", bg: "#EFF6FF" },
  closed:      { color: "#059669", bg: "#ECFDF5" },
  overdue:     { color: "#DC2626", bg: "#FEF2F2" },
};

function CapaPanel({ actions, onChange }) {
  const today = new Date().toISOString().slice(0, 10);
  const [confirmIdx, setConfirmIdx] = useState(null);
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  useEffect(() => {
    const updated = actions.map((a) =>
      a.dueDate && a.dueDate < today && a.status !== "closed" && a.status !== "overdue"
        ? { ...a, status: "overdue" }
        : a
    );
    if (updated.some((a, i) => a.status !== actions[i].status)) onChange(updated);
  }, []); // eslint-disable-line

  const update = (i, field, val) =>
    onChange(actions.map((a, idx) => idx === i ? { ...a, [field]: val } : a));

  const allLowHierarchy = actions.length > 0 &&
    actions.every((a) => a.controlHierarchy === "4_administrative" || a.controlHierarchy === "5_epp");

  return (
    <div>
      {allLowHierarchy && (
        <div style={{ padding: "8px 12px", background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 6, fontSize: "0.8rem", color: "#92400E", marginBottom: "0.75rem" }}>
          ⚠ {t("low_hierarchy_warning")}
        </div>
      )}

      {actions.map((a, i) => {
        const eStatus = (a.dueDate && a.dueDate < today && a.status !== "closed") ? "overdue" : a.status;
        const hcfg = HIERARCHY_COLOR[a.controlHierarchy] || { color: "#9CA3AF", bg: "#F9FAFB" };
        const scfg = STATUS_STYLE[eStatus]               || { color: "#9CA3AF", bg: "#F9FAFB" };
        return (
          <div key={i} className="at-table__capa-card">
            {/* ── Fila 1: tipo · jerarquía · descripción · eliminar ── */}
            <div className="at-table__capa-card-top">
              <select value={a.type} onChange={(e) => update(i, "type", e.target.value)}
                className="at-table__capa-badge-select"
                style={{ color: a.type === "corrective" ? "#1D4ED8" : "#7C3AED", background: a.type === "corrective" ? "#EFF6FF" : "#F5F3FF", borderColor: a.type === "corrective" ? "#BFDBFE" : "#DDD6FE" }}>
                <option value="corrective">{t("corrective")}</option>
                <option value="preventive">{t("preventive")}</option>
              </select>
              <select value={a.controlHierarchy} onChange={(e) => update(i, "controlHierarchy", e.target.value)}
                className="at-table__capa-badge-select"
                style={{ color: hcfg.color, background: hcfg.bg, borderColor: hcfg.color + "55" }}>
                <option value="">{t("hierarchy")}</option>
                {CONTROL_HIERARCHY_OPTIONS.map((o) => <option key={o.key} value={o.value}>{t(o.value)}</option>)}
              </select>
              <input type="text" value={a.description} onChange={(e) => update(i, "description", e.target.value)}
                placeholder={t("action_description_placeholder")} className="at-table__capa-desc-input" />
              <button type="button" onClick={() => setConfirmIdx(i)} className="at-table__capa-del-btn">×</button>
            </div>
            {/* ── Fila 2: responsable · verificador · vence · estado · eficacia ── */}
            <div className="at-table__capa-card-bottom">
              <div className="at-table__capa-field">
                <label>{t("responsible")}</label>
                <input type="text" value={a.responsible} onChange={(e) => update(i, "responsible", e.target.value)} className="at-table__inline-input" placeholder={t("who_executes")} />
              </div>
              <div className="at-table__capa-field">
                <label>{t("verifier")}</label>
                <input type="text" value={a.verifier} onChange={(e) => update(i, "verifier", e.target.value)} className="at-table__inline-input" placeholder={t("who_verifies")} />
              </div>
              <div className="at-table__capa-field">
                <label>{t("due_date")}</label>
                <input type="date" value={a.dueDate} onChange={(e) => update(i, "dueDate", e.target.value)} className="at-table__inline-input" />
              </div>
              <div className="at-table__capa-field">
                <label>{t("status")}</label>
                <select value={eStatus} onChange={(e) => update(i, "status", e.target.value)}
                  className="at-table__inline-select"
                  style={{ color: scfg.color, background: scfg.bg, fontWeight: 600, borderColor: scfg.color + "66" }}>
                  {CAPA_STATUS_OPTIONS.map((o) => <option key={o.key} value={o.value}>{t(o.value)}</option>)}
                </select>
              </div>
              <div className="at-table__capa-field">
                <label>{t("efficacy")}</label>
                <select value={a.efficacy} onChange={(e) => update(i, "efficacy", e.target.value)}
                  className="at-table__inline-select"
                  disabled={eStatus !== "closed"}
                  style={{ opacity: eStatus !== "closed" ? 0.5 : 1 }}>
                  {EFFICACY_OPTIONS.map((o) => <option key={o.key} value={o.value}>{t(o.value)}</option>)}
                </select>
              </div>
            </div>
          </div>
        );
      })}

      <button type="button" onClick={() => onChange([...actions, { ...EMPTY_ACTION }])} className="at-table__capa-add-btn">
        + {t("add_action")}
      </button>

      <ConfirmDialog
        open={confirmIdx !== null}
        title={t("delete_action")}
        message={t("confirm_delete_action")}
        confirmLabel={t("delete")}
        cancelLabel={t("cancel")}
        confirmDanger
        onCancel={() => setConfirmIdx(null)}
        onConfirm={() => { onChange(actions.filter((_, idx) => idx !== confirmIdx)); setConfirmIdx(null); }}
      />
    </div>
  );
}
